import {
  getFirestore,
  runTransaction,
  serverTimestamp,
  increment,
  Timestamp,
  onSnapshot,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  addDoc,
  setDoc,
  updateDoc,
  collection,
  arrayUnion,
  query,
  where,
  orderBy,
  collectionGroup,
  startAfter,
  endBefore,
  writeBatch,
  limit,
  getCountFromServer
} from "firebase/firestore";
import {
  clientStatusForLitigationSubmissionWorkflow,
  litigationSubmissionWorkflowStatusValues
} from "@/models/litigationSubmissions";
import {
  watchTrendWorkflowStatusValues
} from "@/models/watchTrends";
import {
  isPublishedWatchlistStatus,
  normalizeWatchlistPublicationStatus,
  watchlistPublicationStatusValues
} from "@/models/watchlists";
import {
  watchlistQuestionResponseTypeValues
} from "@/models/watchlistQuestions";
import {
  normalizeCourtListenerMonitor
} from "@/models/courtListenerUpdates";
import { normalizeSearchMetadata } from "@/models/searchMetadata";
import { normalizeNotificationTagIds } from "@/models/notificationTags";
import { httpsCallable } from "firebase/functions";

import { buckets } from '@/models/DataTemplates';
import { projectAuth, projectFunctions } from "@/firebase/firebase";

// import CryptoJS from "crypto-js";

// MARK: - Suggested Table of Contents
//
// 1. App Client Records & Support Access
//    fetchUserData
//    requestSupportClient
//    fetchPreparedSupportClient
//    requestAppClientFieldChange
//    fetchPreparedAppClientFieldChange
//    requestAppClientAccountAction
//    fetchPreparedAppClientAccountAction
//    updateSupportTicketStatus
//    fetchSupportTicketCounts
//    fetchUnopenedSupportTickets
//    fetchSupportTickets
//    fetchSupportTicketsByCode
//    fetchEncryptedAppClient
//
// 2. Archived App Clients
//    fetchArchivedClients
//    fetchUserExitChats
//    fetchUserExitChatMessages
//    updateArchivedClientReview
//
// 3. Admin Authentication & Management
//    createAuthRequest
//    verifyAuthRequest
//    checkAdminId
//    checkAdminEmails
//    fetchAdmins
//    saveAdminToFirestore
//    updateAdminAccount
//    saveAdminRequest
//    fetchAdminEmail
//    fetchAdminFile
//    getAdminInfo
//    fetchDevelopmentErrors
//    fetchDevelopmentErrorNotes
//    saveDevelopmentErrorNote
//    fetchDevelopmentErrorBucketStates
//    resolveDevelopmentErrorBucket
//
// 3. App Attorney Requests
//    normalizeRequestStatus
//    fetchAttorneyRequests
//    fetchAttorneyRequestClientInfo
//    updateAttorneyRequestStatus
//    addAttorneyRequestNote
//    fetchAttorneyRequestNotes
//    addAttorneyRequestMessage
//    fetchAttorneyRequestMessages
//    fetchMaixTextThreads
//    fetchMaixTextMessages
//    sendMaixTextMessage
//    markMaixTextThreadRead
//    setMaixTextConversationArchived
//    deleteMaixTextConversation
//
// 4. Litigation Management
//    fetchLitigations
//    fetchLitigationSubmissions
//    fetchLitigationSubmissionClientInfo
//    fetchLitigationSubmissionSession
//    fetchLitigationSubmissionNotes
//    addLitigationSubmissionNote
//    fetchLitigationSubmissionMessages
//    addLitigationSubmissionMessage
//    markLitigationSubmissionReviewed
//    markLitigationSubmissionNeedsReview
//    updateLitigationSubmissionStatus
//    fetchSignedLitigationContractPDF
//    saveLitigation
//    updateLitigation
//    updateLitigationBanner
//
// 5. CourtListener Updates
//    fetchCourtListenerMonitors
//    saveCourtListenerMonitor
//    updateCourtListenerMonitor
//    fetchCourtListenerUpdateEvents
//
// 6. Watchlist Management
//    fetchWatchlists
//    saveWatchlist
//    updateWatchlist
//    fetchWatchTrendReports
//    fetchWatchTrendSession
//    fetchWatchTrendNotes
//    addWatchTrendNote
//    fetchWatchTrendMessages
//    addWatchTrendMessage
//    markWatchTrendReviewed
//    markWatchTrendNeedsReview
//    updateWatchTrendStatus
//
// 6. Watch Intelligence
//    fetchUnsubmittedWatchSeeds
//    fetchWatchingCompanies
//    fetchAllUnsubmittedWatchSeeds
//    fetchChatStreamCount
//    slugify
//    confirmCompanyAndAppendSeeds
//    fetchWatchingCosArray
//    fetchWatchingCompanyDocs
//    watchWatchingCompanies
//    watchCompanySeeds
//    deleteCompanySeeds
//    deleteUnconfirmedSeeds
//
// 7. Dashboard Metrics
//    fetchDashboardDocument
//    dashboardNumber
//    dashboardPercentage
//    dashboardDate
//    fetchRecentSignInCounts
//    fetchAttorneyRequestCounts
//    fetchAppClientDashboard
//
// 8. Lawyers - Legacy Candidate
//    fetchAttyData
//    fetchAttyIdData
//    checkAttyEmails
//    saveAttorneyRequest
//    updateAttorney
//    fetchNewAttyRequests
//
// 9. Chats & Messaging - Legacy Candidate
//    fetchChats
//    fetchClientChat
//    handleSendMessage
//    sendLitChatReqMessage
//    listenToChatUpdates
//    listenToLitChatReq
//
// 10. Widgets & Data Explorer - Legacy Candidate
//    saveWidgetToFirestore
//    fetchWidgets
//    fetchWidgetDocs
//    getBucketData
//    updateDocumentField
//
// 11. Litigation Case Management - Legacy Candidate
//    fetchAuthorizedLitigations
//    fetchLitigationCases
//    fetchLitigationClients
//    updateLitFeedMeta
//    updateAttentionCountForLitigation
//    fetchClientChatRequests
//    fetchClientInfo

const db = getFirestore();
// const ENCRYPTION_KEY = "YOUR_SECRET_KEY"; // Replace with a secure key

const normalizeSupportTicketStatus = status => {
  const normalizedStatus = String(status ?? "open").trim().toLowerCase();

  if (normalizedStatus === "emailed" || normalizedStatus === "expired") {
    return "open";
  }

  return normalizedStatus;
};

const normalizeSupportTicket = ticket => ({
  ...ticket,
  status: normalizeSupportTicketStatus(ticket?.status)
});

const supportTicketDisplayStatus = ticket =>
  normalizeSupportTicketStatus(ticket?.status) === "open" && !ticket?.openedAt
    ? "new"
    : normalizeSupportTicketStatus(ticket?.status);

const supportTicketPriority = {
  new: 0,
  open: 1,
  replied: 2,
  pending: 3,
  resolved: 4,
  closed: 5
};

const supportTicketTimestamp = value =>
  value?.toMillis?.() ??
  value?.toDate?.()?.getTime?.() ??
  (value instanceof Date ? value.getTime() : 0);

const sortSupportTickets = tickets =>
  [...tickets].sort((left, right) => {
    const priorityDifference =
      supportTicketPriority[supportTicketDisplayStatus(left)] -
      supportTicketPriority[supportTicketDisplayStatus(right)];

    return priorityDifference ||
      supportTicketTimestamp(right.createdAt) - supportTicketTimestamp(left.createdAt);
  });

// const encrypt = (data) => {
//   return CryptoJS.AES.encrypt(data, ENCRYPTION_KEY).toString();
// };

// MARK: - 1. App Client Records & Support Access

export const fetchUserData = async (userId) => {
  try {
    const userDocRef = doc(db, 'clients', userId);
    const userDoc = await getDoc(userDocRef);

    if (userDoc.exists()) {
      return userDoc.data();
    } else {
      console.error('No such document!');
      return null;
    }
  } catch (error) {
    console.error('Error fetching user data:', error.message);
    throw error;
  }
};

export const requestSupportClient = async (ticket, adminId) => {
  if (!ticket?.id || !ticket?.clientId) {
    throw new Error("A valid Support Ticket is required.");
  }

  const requestRef = doc(collection(db, "admin", "supportAccess", "requests"));

  await setDoc(requestRef, {
    requestId: requestRef.id,
    ticketId: ticket.id,
    clientId: ticket.clientId,
    adminId,
    status: "requested",
    requestedAt: serverTimestamp()
  });

  return requestRef.id;
};

export const fetchPreparedSupportClient = async requestId => {
  if (!requestId) {
    throw new Error("A support access request ID is required.");
  }

  const snapshot = await getDoc(doc(db, "admin", "supportAccess", "requests", requestId));

  if (!snapshot.exists()) {
    return null;
  }

  const data = snapshot.data();

  if (data.status === "failed") {
    throw new Error(data.error ?? "Unable to prepare the App Client.");
  }

  if (data.status !== "ready") {
    return null;
  }

  return {
    client: data.client ?? null,
    tickets: (data.tickets ?? []).map(normalizeSupportTicket)
  };
};

export const requestAppClientFieldChange = async ({ clientId, ticketId, field, value }) => {
  const allowedFields = ["name", "mobile", "state"];

  if (!clientId || !ticketId || !allowedFields.includes(field) || typeof value !== "string") {
    throw new Error("A valid App Client, Support Ticket, field, and value are required.");
  }

  const requestRef = doc(collection(db, "admin", "supportAccess", "clientChanges"));
  const payload = {
    requestId: requestRef.id,
    clientId,
    ticketId,
    field,
    value,
    status: "requested",
    requestedAt: serverTimestamp()
  };

  console.log("[NAME CHANGE] ➡ Creating Name Change request", {
    collectionPath: "admin/supportAccess/clientChanges",
    documentPath: requestRef.path,
    payload
  });

  await setDoc(requestRef, payload);

  console.log("[NAME CHANGE] ✅ Request document created", { requestId: requestRef.id });

  return requestRef.id;
};

export const fetchPreparedAppClientFieldChange = async requestId => {
  if (!requestId) {
    throw new Error("A Client Field Change request ID is required.");
  }

  const requestRef = doc(db, "admin", "supportAccess", "clientChanges", requestId);

  console.log("[NAME CHANGE] ➡ Fetching Name Change request", {
    requestId,
    documentPath: requestRef.path
  });

  const snapshot = await getDoc(requestRef);

  console.log("[NAME CHANGE] 📄 Request document:", {
    exists: snapshot.exists(),
    data: snapshot.data()
  });

  if (!snapshot.exists()) { return null; }

  const data = snapshot.data();

  if (data.status === "failed") {
    throw new Error(data.error ?? "Unable to update the App Client field.");
  }

  return data.status === "ready"
    ? data.client ?? null
    : null;
};

export const requestAppClientAccountAction = async ({ clientId, ticketId, action }) => {
  const allowedActions = ["force_logout"];

  if (!clientId || !ticketId || !allowedActions.includes(action)) {
    throw new Error("A valid App Client, Support Ticket, and account action are required.");
  }

  const requestRef = doc(collection(db, "admin", "supportAccess", "accountActions"));

  await setDoc(requestRef, {
    requestId: requestRef.id,
    action,
    clientId,
    ticketId,
    status: "requested",
    requestedAt: serverTimestamp()
  });

  return requestRef.id;
};

export const fetchPreparedAppClientAccountAction = async requestId => {
  if (!requestId) {
    throw new Error("An Account Action request ID is required.");
  }

  const snapshot = await getDoc(
    doc(db, "admin", "supportAccess", "accountActions", requestId)
  );

  if (!snapshot.exists()) { return null; }

  const data = snapshot.data();

  if (data.status === "failed") {
    throw new Error(data.error ?? "Unable to complete the App Client account action.");
  }

  return data.status === "completed"
    ? data.result ?? { success: true }
    : null;
};

export const updateSupportTicketStatus = async ({ clientId, ticketId, status }) => {
  const allowedStatuses = ["open", "replied", "pending", "resolved", "closed"];

  if (!clientId || !ticketId) {
    throw new Error("A valid Support Ticket is required.");
  }

  if (!allowedStatuses.includes(status)) {
    throw new Error("A valid Support Ticket status is required.");
  }

  const updates = {
    status,
    updatedAt: serverTimestamp()
  };

  if (status === "resolved") {
    updates.resolvedAt = serverTimestamp();
  }

  await updateDoc(doc(db, "users", clientId, "tickets", ticketId), updates);
};

const fetchSupportTicketCounts = async () => {
  try {
    const ticketsRef = collectionGroup(db, "tickets");
    const openTicketsQuery = query(
      ticketsRef,
      // Legacy emailed/expired documents remain readable until a separate migration.
      where("status", "in", ["open", "replied", "pending", "emailed", "expired"]),
      where("expiresAt", ">", Timestamp.now())
    );

    const openSnapshot = await getCountFromServer(openTicketsQuery);

    return {
      openTickets: dashboardNumber(openSnapshot.data().count)
    };
  } catch (error) {
    console.warn("⚠️ Unable to load Support Ticket counts:", error);

    return {
      openTickets: 0
    };
  }
};

const fetchUnopenedSupportTickets = async () => {
  try {
    const ticketsQuery = query(
      collectionGroup(db, "tickets"),
      where("status", "==", "open"),
      where("expiresAt", ">", Timestamp.now()),
      orderBy("expiresAt", "asc")
    );

    const snapshot = await getDocs(ticketsQuery);

    return snapshot.docs
      .map(document => ({
        id: document.id,
        path: document.ref.path,
        ...document.data()
      }))
      .filter(ticket => !ticket.openedAt)
      .map(normalizeSupportTicket)
      .sort((left, right) =>
        supportTicketTimestamp(right.createdAt) - supportTicketTimestamp(left.createdAt)
      );
  } catch (error) {
    console.warn("⚠️ Unable to load unopened Support Tickets:", error);

    return [];
  }
};

export const fetchSupportTickets = async ({
  statusFilter = "needs-action",
  timeFilter = "30-days",
  maxResults = 100
} = {}) => {
  try {
    const ticketsRef = collectionGroup(db, "tickets");
    const constraints = [];

    /*
       Status
      */

    switch (statusFilter) {
      case "needs-action":
        constraints.push(where("status", "in", ["open", "replied", "pending"]));
        break;

      case "new":
        // "New" is derived from open + no openedAt.
        constraints.push(where("status", "==", "open"));
        break;

      case "all":
        break;

      default:
        constraints.push(where("status", "==", statusFilter));
        break;
    }

    /*
       Time period
      */

    const now = Date.now();
    let cutoff = null;

    switch (timeFilter) {
      case "7-days":
        cutoff = now - ( 7 * 24 * 60 * 60 * 1000);
        break;

      case "30-days":
        cutoff = now - ( 30 * 24 * 60 * 60 * 1000);
        break;

      case "90-days":
        cutoff = now - ( 90 * 24 * 60 * 60 * 1000);
        break;

      case "all-time":
      default:
        break;
    }

    if (cutoff) {
      constraints.push(where("createdAt", ">=", Timestamp.fromMillis(cutoff)));
    }

    constraints.push(orderBy("createdAt", "desc"), limit(maxResults));

    const ticketsQuery = query(ticketsRef, ...constraints);
    const snapshot = await getDocs(ticketsQuery);

    let tickets = snapshot.docs.map(document =>
      normalizeSupportTicket({
        id: document.id,
        path: document.ref.path,
        ...document.data()
      })
    );

    /*
       Firestore cannot cleanly query for a field that
       does not exist, so derive New after fetching.
      */

    if (statusFilter === "new") {
      tickets = tickets.filter(ticket => !ticket.openedAt);
    }

    return sortSupportTickets(tickets);
  } catch (error) {
    console.error("❌ Unable to fetch Support Tickets:", error);

    throw error;
  }
};

export const fetchSupportTicketsByCode = async code => {
  const normalizedCode = String(code ?? "")
    .replace(/\D/g, "")
    .slice(0, 6);

  if (!/^\d{6}$/.test(normalizedCode)) {
    throw new Error("A valid 6-digit Support Ticket code is required.");
  }

  const ticketsQuery = query(
    collectionGroup(db, "tickets"),
    where("code", "==", normalizedCode)
  );

  const snapshot = await getDocs(ticketsQuery);

  return snapshot.docs
    .map(document =>
      normalizeSupportTicket({
        id: document.id,
        path: document.ref.path,
        ...document.data()
      })
    )
    .sort((left, right) =>
      supportTicketTimestamp(right.createdAt) - supportTicketTimestamp(left.createdAt)
    );
};

export const fetchEncryptedAppClient = async uid => {
  if (!uid) {
    throw new Error("A client UID is required.");
  }

  const snapshot = await getDoc(doc(db, "clients", uid));

  if (!snapshot.exists()) {
    return null;
  }

  return {
    id: snapshot.id,
    ...snapshot.data()
  };
};

// MARK: - 2. Archived App Clients

const archivedClientsRef = () => collection(db, "admin", "archives", "clients");

const archiveTimestamp = value => {
  if (value?.toMillis) { return value.toMillis(); }
  if (value instanceof Date) { return value.getTime(); }

  return new Date(value ?? 0).getTime();
};

export const fetchArchivedClients = async ({ email = "", maxResults = 100 } = {}) => {
  const normalizedEmail = String(email ?? "").trim().toLowerCase();
  const safeLimit = Math.min(Math.max(Number(maxResults) || 100, 1), 200);
  let documents = [];

  if (normalizedEmail) {
    const fields = [
      "authEmailNormalized",
      "notificationEmailNormalized",
      "authEmail",
      "notificationEmail"
    ];
    const snapshots = await Promise.all(fields.map(field => getDocs(query(
      archivedClientsRef(),
      where(field, "==", normalizedEmail),
      limit(safeLimit)
    ))));
    const uniqueDocuments = new Map();

    snapshots.forEach(snapshot => snapshot.docs.forEach(document => {
      uniqueDocuments.set(document.id, document);
    }));
    documents = [...uniqueDocuments.values()];
  } else {
    const snapshot = await getDocs(query(
      archivedClientsRef(),
      orderBy("archivedAt", "desc"),
      limit(safeLimit)
    ));
    documents = snapshot.docs;
  }

  return documents
    .map(document => ({ id: document.id, ...document.data() }))
    .sort((left, right) => archiveTimestamp(right.archivedAt) - archiveTimestamp(left.archivedAt));
};

export const fetchUserExitChats = async ({ maxResults = 100 } = {}) => {
  const safeLimit = Math.min(Math.max(Number(maxResults) || 100, 1), 200);
  const snapshot = await getDocs(query(
    archivedClientsRef(),
    where("exitFeedbackProvided", "==", true),
    limit(safeLimit)
  ));

  return snapshot.docs
    .map(document => ({ id: document.id, ...document.data() }))
    .sort((left, right) => {
      return archiveTimestamp(right.exitFeedbackLastAt) - archiveTimestamp(left.exitFeedbackLastAt);
    });
};

export const fetchUserExitChatMessages = async archive => {
  const uid = String(archive?.uid ?? archive?.id ?? "").trim();
  const conversationId = String(archive?.exitConversationId ?? "account-closure").trim();

  if (!uid || !conversationId) {
    throw new Error("A valid User Exit Chat is required.");
  }

  const messagesRef = collection(
    db,
    "admin",
    "archives",
    "clients",
    uid,
    "exitChats",
    conversationId,
    "messages"
  );
  const snapshot = await getDocs(query(messagesRef, orderBy("sequence", "asc")));

  return snapshot.docs.map(document => ({ id: document.id, ...document.data() }));
};

export const updateArchivedClientReview = async review => {
  const updateReview = httpsCallable(projectFunctions, "updateArchivedClientReview");
  const result = await updateReview(review);

  return result.data;
};

// MARK: - 3. Admin Authentication & Management

const legalDocumentIds = [
  "limited-scope-agreement",
  "privacy-policy",
  "app-terms-conditions",
  "ai-confidentiality",
  "example-app-contract"
];

export const fetchLegalDocuments = async () => {
  const snapshot = await getDocs(collection(db, "legalContent"));

  return snapshot.docs
    .map(document => ({
      documentId: document.id,
      ...document.data()
    }))
    .filter(document => legalDocumentIds.includes(document.documentId));
};

export const fetchPublishedLegalDocument = async documentId => {
  const normalizedDocumentId = String(documentId ?? "").trim();

  if (!legalDocumentIds.includes(normalizedDocumentId)) {
    throw new Error("Unsupported legal document.");
  }

  const snapshot = await getDoc(doc(db, "legalContent", normalizedDocumentId));

  return snapshot.exists()
    ? { documentId: snapshot.id, ...snapshot.data() }
    : null;
};

export const fetchLegalDocumentDrafts = async () => {
  const drafts = await Promise.all(legalDocumentIds.map(async documentId => {
    const snapshot = await getDoc(doc(db, "legalContent", documentId, "drafts", "current"));
    return snapshot.exists()
      ? { documentId, ...snapshot.data() }
      : null;
  }));

  return drafts.filter(Boolean);
};

export const saveLegalDocumentDraft = async legalDocument => {
  const documentId = String(legalDocument?.documentId ?? "").trim();
  const title = String(legalDocument?.title ?? "").trim();
  const content = String(legalDocument?.content ?? "").trim();
  const reviewMode = String(legalDocument?.reviewMode ?? "").trim();
  const basedOnVersionId = String(legalDocument?.basedOnVersionId ?? "").trim();
  const adminId = String(legalDocument?.adminId ?? "").trim();
  const adminName = String(legalDocument?.adminName ?? "").trim();
  const authenticatedAdminId = projectAuth.currentUser?.uid ?? "";

  if (!legalDocumentIds.includes(documentId)) {
    throw new Error("Unsupported legal document.");
  }

  if (!title || !content) {
    throw new Error("The title and draft language are required.");
  }

  if (!["acknowledgment", "signature", "reference"].includes(reviewMode)) {
    throw new Error("Unsupported app review mode.");
  }

  if (!adminId || adminId !== authenticatedAdminId) {
    throw new Error("The authenticated administrator could not be verified.");
  }

  await setDoc(doc(db, "legalContent", documentId, "drafts", "current"), {
    schemaVersion: 1,
    documentId,
    title,
    contentFormat: "markdown",
    content,
    reviewMode,
    basedOnVersionId,
    updatedAt: serverTimestamp(),
    updatedBy: adminId,
    updatedByName: adminName
  });
};

export const publishLegalDocumentVersion = async legalDocument => {
  const documentId = String(legalDocument?.documentId ?? "").trim();
  const title = String(legalDocument?.title ?? "").trim();
  const content = String(legalDocument?.content ?? "").trim();
  const reviewMode = String(legalDocument?.reviewMode ?? "").trim();
  const adminId = String(legalDocument?.adminId ?? "").trim();
  const adminName = String(legalDocument?.adminName ?? "").trim();
  const clearDraft = legalDocument?.clearDraft === true;
  const authenticatedAdminId = projectAuth.currentUser?.uid ?? "";

  if (!legalDocumentIds.includes(documentId)) {
    throw new Error("Unsupported legal document.");
  }

  if (!title || !content) {
    throw new Error("The title and legal document language are required.");
  }

  if (!["acknowledgment", "signature", "reference"].includes(reviewMode)) {
    throw new Error("Unsupported app review mode.");
  }

  if (!adminId || adminId !== authenticatedAdminId) {
    throw new Error("The authenticated administrator could not be verified.");
  }

  const currentRef = doc(db, "legalContent", documentId);
  const draftRef = doc(currentRef, "drafts", "current");

  return runTransaction(db, async transaction => {
    const currentSnapshot = await transaction.get(currentRef);
    const currentRevision = Number(currentSnapshot.data()?.revision ?? 0);
    const revision = currentRevision + 1;
    const versionDate = new Date().toISOString().slice(0, 10);
    const versionId = `${versionDate}-r${revision}`;
    const versionRef = doc(currentRef, "versions", versionId);
    const sharedFields = {
      schemaVersion: 1,
      documentId,
      title,
      contentFormat: "markdown",
      content,
      reviewMode,
      revision,
      versionId,
      effectiveAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: adminId,
      updatedByName: adminName
    };

    transaction.set(currentRef, sharedFields);
    transaction.set(versionRef, {
      ...sharedFields,
      publishedAt: serverTimestamp()
    });
    if (clearDraft) {
      transaction.delete(draftRef);
    }

    return { documentId, versionId, revision };
  });
};

// MARK: - 3. Admin Authentication & Management

export const createAuthRequest = async (phone, password) => {
  console.log(phone, password);

  try {
    await addDoc(collection(db, "admin/watch/authReqs"), {
      phone: phone, // encrypt(phone),
      pw: password, // encrypt(password),
      email: "",
      fbEm: "",
      code: "",
      createdAt: serverTimestamp(),
      expiresAt: Timestamp.fromMillis(Date.now() + 6 * 60 * 60 * 1000),
    });
  } catch (error) {
    console.error("Error creating auth request:", error);
    throw error;
  }
};

export const verifyAuthRequest = async (phone, code) => {
  try {
    const q = query(
      collection(db, "admin/watch/authReqs"),
      where("phone", "==", phone),
      orderBy("createdAt", "desc"),
      limit(1)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      console.log("No matching auth requests found.");
      return null;
    }

    const authData = querySnapshot.docs[0].data();

    if (String(authData.code).trim() !== String(code).trim()) {
      console.error("Invalid verification code.");
      return null;
    }

    return { email: authData.fbEm, password: authData.pw };
  } catch (error) {
    console.error("Error verifying authentication request:", error.message);
    throw error;
  }
};

export const checkAdminId = async (adminId) => {
  try {
    const authRef = doc(db, "admin", "auths");
    const authDoc = await getDocFromServer(authRef);

    if (!authDoc.exists()) {
      console.warn("⚠️ admin/auths document does not exist.");
      return false;
    }

    const data = authDoc.data();
    const adminIds = Array.isArray(data?.admins) ? data.admins : [];

    return adminIds.includes(adminId);
  } catch (error) {
    console.error("❌ Error checking administrator UID:", error.message);
    throw error;
  }
};

export const checkAdminEmails = async (adminEm) => {
  try {
    const authRef = doc(db, "admin", "auths");
    const authDoc = await getDoc(authRef);

    if (!authDoc.exists()) {
      console.warn("⚠️ admin/auths document does not exist.");
      return false;
    }

    const data = authDoc.data();
    const adminEms = data?.adminEms || [];

    return adminEms.includes(adminEm);
  } catch (error) {
    console.error("❌ Error checking email:", error.message);
    throw error;
  }
};

export const fetchAdmins = async () => {
  try {
    const adminsRef = collection(db, "admin", "auths", "users");
    const snapshot = await getDocs(adminsRef);
    const admins = snapshot.docs.map(adminDocument => {
      const data = adminDocument.data();

      return {
        id: adminDocument.id,
        adminId: data.adminId ?? adminDocument.id,
        name: data.name ?? "Unnamed Admin",
        email: data.email ?? "",
        type: Array.isArray(data.type) ? data.type : []
      };
    });

    admins.sort((first, second) => {
      return first.name.localeCompare(second.name);
    });

    return admins;
  } catch (error) {
    console.error("❌ Error fetching admins:", error);

    return [];
  }
};

export const saveAdminToFirestore = async (adminData) => {
  try {
    const createAdminRequest = httpsCallable(projectFunctions, "createAdminAccountRequest");
    const result = await createAdminRequest(adminData);

    return result.data;
  } catch (error) {
    console.error("❌ Error adding admin:", error);
    throw error;
  }
};

export const updateAdminAccount = async adminData => {
  try {
    const updateAdmin = httpsCallable(projectFunctions, "updateAdminAccount");
    const result = await updateAdmin(adminData);

    return result.data;
  } catch (error) {
    console.error("❌ Error updating admin:", error);
    throw error;
  }
};

export const saveAdminRequest = async (reqData) => {
  try {
    const docRef = await addDoc(collection(db, "admin/watch/adminReqs"), reqData);
    console.log(`✅ Admin data request sent...`);
    return docRef.id
  } catch (error) {
    console.error("❌ Error sending request:", error.message);
    throw error;
  }
};

export const fetchAdminEmail = async (adminId) => {
  if (!adminId) {
    console.error("❌ No admin ID provided.");
    return null;
  }

  try {
    const adminRef = doc(db, `admin/auths/users/${adminId}`);
    const adminSnap = await getDoc(adminRef);

    if (!adminSnap.exists()) {
      console.error("❌ Admin account not found.");
      return null;
    }

    const adminData = adminSnap.data();

    if (!adminData.type || !adminData.type.includes("litigations")) {
      console.error("❌ Admin does not have access to litigations.");
      return null;
    }

    return adminData.email || null;
  } catch (error) {
    console.error("❌ Error fetching admin data:", error);
    return null;
  }
};

export const fetchAdminFile = async (adminId) => {
  if (!adminId) {
    console.error("❌ No admin ID provided.");
    return null;
  }

  try {
    const adminRef = doc(db, `admin/auths/users/${adminId}`);
    const adminSnap = await getDoc(adminRef);

    if (!adminSnap.exists()) {
      console.error("❌ Admin account not found.");
      return null;
    }

    const adminData = adminSnap.data();

    if (!adminData.type || !adminData.type.includes("litigations")) {
      console.error("❌ Admin does not have access to litigations.");
      return null;
    }

    return adminData; // ✅ Return full document instead of just email
  } catch (error) {
    console.error("❌ Error fetching admin data:", error);
    return null;
  }
};

export const getAdminInfo = async (userId) => {
  try {
    console.log("🔍 Checking admin access for:", userId);

    const adminDocRef = doc(db, "admin", "auths", "users", userId);
    const adminDocSnap = await getDoc(adminDocRef);

    if (adminDocSnap.exists()) {
      const adminData = adminDocSnap.data();
      console.log("✅ Admin Data:", adminData);

      // Extract values
      const isAdmin = adminData.adminId === userId; // Confirm admin
      const accessTypes = adminData.type || []; // Get types array
      const name = String(adminData.name ?? "").trim();

      return { isAdmin, accessTypes, name };
    }

    console.warn("⚠️ No admin data found for this user.");
    return { isAdmin: false, accessTypes: [] };
  } catch (error) {
    console.error("❌ Error fetching admin data:", error.message);
    return { isAdmin: false, accessTypes: [] };
  }
};

export const fetchDevelopmentErrors = async () => {
  const errorsRef = collection(db, "admin", "dev", "errors");
  const snapshot = await getDocs(errorsRef);

  return snapshot.docs.map(errorDocument => {
    const data = errorDocument.data();

    return {
      id: errorDocument.id,
      error: String(data.error ?? "Unknown error"),
      lfClientId: String(data.lfClientId ?? ""),
      whErr: String(data.whErr ?? "Unknown location"),
      severity: String(data.severity ?? "info").toLowerCase(),
      errRecordedAt: data.errRecordedAt?.toDate?.() ?? null
    };
  });
};

const normalizeDevelopmentErrorObject = errorObject => ({
  error: String(errorObject?.error ?? ""),
  whErr: String(errorObject?.whErr ?? ""),
  severity: String(errorObject?.severity ?? "info").toLowerCase()
});

const developmentErrorBucketId = async errorObject => {
  const normalizedError = normalizeDevelopmentErrorObject(errorObject);
  const bucketValue = JSON.stringify([
    normalizedError.error,
    normalizedError.whErr,
    normalizedError.severity
  ]);
  const encodedValue = new TextEncoder().encode(bucketValue);
  const digest = await crypto.subtle.digest("SHA-256", encodedValue);

  return Array.from(new Uint8Array(digest))
    .map(value => value.toString(16).padStart(2, "0"))
    .join("");
};

export const fetchDevelopmentErrorNotes = async errorObject => {
  const bucketId = await developmentErrorBucketId(errorObject);
  const notesRef = collection(db, "admin", "dev", "notes", bucketId, "notes");
  const notesQuery = query(notesRef, orderBy("savedAt", "desc"));
  const snapshot = await getDocs(notesQuery);

  return snapshot.docs.map(noteDocument => {
    const data = noteDocument.data();

    return {
      id: noteDocument.id,
      content: String(data.content ?? ""),
      noteTakerId: String(data.noteTakerId ?? ""),
      noteTakerName: String(data.noteTakerName ?? "Admin"),
      savedAt: data.savedAt?.toDate?.() ?? null
    };
  });
};

export const saveDevelopmentErrorNote = async (errorObject, noteData) => {
  const content = String(noteData?.content ?? "").trim();
  const noteTakerId = String(noteData?.noteTakerId ?? "").trim();
  const noteTakerName = String(noteData?.noteTakerName ?? "").trim();

  if (!content || !noteTakerId || !noteTakerName) {
    throw new Error("The note, admin ID, and note taker name are required.");
  }

  const normalizedError = normalizeDevelopmentErrorObject(errorObject);
  const bucketId = await developmentErrorBucketId(normalizedError);
  const bucketRef = doc(db, "admin", "dev", "notes", bucketId);
  const noteRef = doc(collection(bucketRef, "notes"));
  const batch = writeBatch(db);

  batch.set(bucketRef, {
    errorObject: normalizedError,
    lastNoteAdded: serverTimestamp()
  }, { merge: true });

  batch.set(noteRef, {
    content,
    noteTakerId,
    noteTakerName,
    savedAt: serverTimestamp()
  });

  await batch.commit();

  return noteRef.id;
};

export const fetchDevelopmentErrorBucketStates = async () => {
  const statesRef = collection(db, "admin", "dev", "notes");
  const snapshot = await getDocs(statesRef);

  return snapshot.docs.map(stateDocument => {
    const data = stateDocument.data();

    return {
      id: stateDocument.id,
      errorObject: normalizeDevelopmentErrorObject(data.errorObject),
      status: String(data.status ?? "open").toLowerCase(),
      resolvedAt: data.resolvedAt?.toDate?.() ?? null,
      resolvedById: String(data.resolvedById ?? ""),
      resolvedByName: String(data.resolvedByName ?? ""),
      resolvedOccurrenceCount: Number(data.resolvedOccurrenceCount ?? 0),
      resolvedThroughRecordedAt:
        data.resolvedThroughRecordedAt?.toDate?.() ?? null
    };
  });
};

export const resolveDevelopmentErrorBucket = async (errorObject, resolutionData) => {
  const resolvedById = String(resolutionData?.resolvedById ?? "").trim();
  const resolvedByName = String(resolutionData?.resolvedByName ?? "").trim();
  const resolvedOccurrenceCount = Number(resolutionData?.resolvedOccurrenceCount ?? 0);

  if (!resolvedById || !resolvedByName || resolvedOccurrenceCount < 1) {
    throw new Error("Valid resolution audit information is required.");
  }

  const bucketId = await developmentErrorBucketId(errorObject);
  const bucketRef = doc(db, "admin", "dev", "notes", bucketId);
  const resolvedThroughRecordedAt =
    resolutionData?.resolvedThroughRecordedAt instanceof Date
      ? resolutionData.resolvedThroughRecordedAt
      : null;

  await updateDoc(bucketRef, {
    status: "resolved",
    resolvedAt: serverTimestamp(),
    resolvedById,
    resolvedByName,
    resolvedOccurrenceCount,
    resolvedThroughRecordedAt
  });
};

// MARK: - 3. App Attorney Requests

const normalizeRequestStatus = status => {
  if (typeof status !== "string" || status.trim() === "") {
    return "new";
  }

  const normalized = status.trim().toLowerCase();

  return normalized === "submitted" ? "new" : normalized;
};

export const fetchAttorneyRequests = async () => {
  try {
    const requestsRef = collection(db, "requests");
    const snapshot = await getDocs(requestsRef);

    return snapshot.docs.map(requestDocument => {
      const data = requestDocument.data();

      return {
        id: data.id ?? requestDocument.id,
        clientId: data.clientId ?? "",
        sourceChatId: data.sourceChatId ?? "",
        createdAt: data.createdAt ?? null,
        updatedAt: data.updatedAt ?? null,
        jurisdiction: data.jurisdiction ?? "",
        issues: Array.isArray(data.issues)
          ? data.issues.map(issue => ({
              id: issue.id ?? crypto.randomUUID(),
              topic: issue.topic ?? "Unidentified Issue",
              summary: issue.summary ?? "",
              probabilityScore:
                typeof issue.probabilityScore === "number" ? issue.probabilityScore : 0
            }))
          : [],
        preferredContactMethods: Array.isArray(data.preferredContactMethods)
          ? data.preferredContactMethods
          : [],
        allowOutsideAttorneys: data.allowOutsideAttorneys ?? false,
        timeSensitiveUnderstandingIo: data.timeSensitiveUnderstandingIo ?? false,
        relationshipAgreementIo: data.relationshipAgreementIo ?? false,
        internalNotes: Array.isArray(data.internalNotes) ? data.internalNotes : [],
        requestChatId: data.requestChatId ?? null,
        status: normalizeRequestStatus(data.status)
      };
    });
  } catch (error) {
    console.error("❌ Error fetching attorney requests:", error);

    throw error;
  }
};

export const fetchAttorneyRequestClientInfo = async requestId => {
  const safeRequestId = String(requestId ?? "").trim();

  if (!safeRequestId) {
    throw new Error("An attorney request ID is required.");
  }

  const fetchClientInfo = httpsCallable(projectFunctions, "getAttorneyRequestClientInfo");
  const result = await fetchClientInfo({ requestId: safeRequestId });
  const data = result.data ?? {};

  return {
    name: String(data.name ?? "").trim(),
    mobile: typeof data.mobile === "string" ? data.mobile.trim() : null,
    contactEm: typeof data.contactEm === "string" ? data.contactEm.trim() : null
  };
};

export const updateAttorneyRequestStatus = async (requestId, status) => {
  try {
    if (!requestId) {
      throw new Error("A request ID is required.");
    }

    const requestRef = doc(db, "requests", requestId);

    await updateDoc(requestRef, {
      status,
      updatedAt: serverTimestamp()
    });

    return true;
  } catch (error) {
    console.error("❌ Unable to update request status:", error);

    return false;
  }
};

export const addAttorneyRequestNote = async (requestId, { text, authorId }) => {
  try {
    if (!requestId) {
      throw new Error("A request ID is required.");
    }

    const trimmedText = String(text ?? "").trim();

    if (!trimmedText) {
      throw new Error("An internal note cannot be empty.");
    }

    const requestRef = doc(db, "requests", requestId);
    const noteRef = doc(collection(requestRef, "notes"));
    const createdAt = Timestamp.now();
    const note = {
      id: noteRef.id,
      requestId,
      text: trimmedText,
      authorId: authorId ?? "",
      createdAt
    };

    const batch = writeBatch(db);

    batch.set(noteRef, note);

    batch.update(requestRef, {
      updatedAt: createdAt,
      lastInternalNoteAt: createdAt
    });

    await batch.commit();

    console.log("✅ Internal request note saved:", noteRef.id);

    return note;
  } catch (error) {
    console.error("❌ Unable to add request note:", error);

    return null;
  }
};

export const fetchAttorneyRequestNotes = async requestId => {
  try {
    if (!requestId) {
      throw new Error("A request ID is required.");
    }

    const notesRef = collection(db, "requests", requestId, "notes");
    const notesQuery = query(notesRef, orderBy("createdAt", "desc"));
    const snapshot = await getDocs(notesQuery);

    return snapshot.docs.map(noteDocument => ({
      id: noteDocument.id,
      ...noteDocument.data()
    }));
  } catch (error) {
    console.error("❌ Unable to fetch request notes:", error);

    throw error;
  }
};

export const addAttorneyRequestMessage = async (requestId, { text, authorId, visibleToClient }) => {
  try {
    if (!requestId) {
      throw new Error("A request ID is required.");
    }

    const trimmedText = String(text ?? "").trim();

    if (!trimmedText) {
      throw new Error("A Request Message cannot be empty.");
    }

    const requestRef = doc(db, "requests", requestId);
    const messageRef = doc(collection(requestRef, "messages"));
    const createdAt = Timestamp.now();
    const message = {
      id: messageRef.id,
      requestId,
      text: trimmedText,
      authorId: authorId ?? "",
      visibleToClient: visibleToClient === true,
      status: "sent",
      createdAt
    };

    const batch = writeBatch(db);

    batch.set(messageRef, message);

    batch.update(requestRef, {
      updatedAt: createdAt,
      lastRequestMessageAt: createdAt
    });

    await batch.commit();

    console.log("✅ Request Message saved:", messageRef.id);

    return message;
  } catch (error) {
    console.error("❌ Unable to add Request Message:", error);

    return null;
  }
};

export const fetchAttorneyRequestMessages = async requestId => {
  try {
    if (!requestId) {
      throw new Error("A request ID is required.");
    }

    const messagesRef = collection(db, "requests", requestId, "messages");
    const messagesQuery = query(messagesRef, orderBy("createdAt", "desc"));
    const snapshot = await getDocs(messagesQuery);

    return snapshot.docs.map(messageDocument => ({
      id: messageDocument.id,
      ...messageDocument.data()
    }));
  } catch (error) {
    console.error("❌ Unable to fetch Request Messages:", error);

    throw error;
  }
};

export const fetchMaixTextThreads = async (conversationStatus = "active") => {
  const safeStatus = conversationStatus === "archived" ? "archived" : "active";
  const fetchThreads = httpsCallable(projectFunctions, "listSmsThreads");
  const result = await fetchThreads({ limit: 100, conversationStatus: safeStatus });

  return Array.isArray(result.data?.threads) ? result.data.threads : [];
};

export const fetchMaixTextMessages = async threadId => {
  const safeThreadId = String(threadId ?? "").trim();

  if (!safeThreadId) {
    throw new Error("A Maix Text conversation is required.");
  }

  const fetchMessages = httpsCallable(projectFunctions, "getSmsThreadMessages");
  const result = await fetchMessages({ threadId: safeThreadId, limit: 250 });

  return Array.isArray(result.data?.messages) ? result.data.messages : [];
};

export const fetchMaixClientMessages = async data => {
  const result = await httpsCallable(projectFunctions, "getMaixClientMessages", { timeout: 120000 })(data);
  return result.data;
};

export const fetchMaixMessageClients = async () => {
  const result = await httpsCallable(projectFunctions, "listMaixMessageClients", { timeout: 120000 })({});
  return result.data.clients;
};

export const sendMaixRequestMessage = async data => {
  const result = await httpsCallable(projectFunctions, "sendMaixRequestMessage", { timeout: 120000 })(data);
  return result.data;
};

export const retryMaixRequestMessage = async messageId => {
  const result = await httpsCallable(projectFunctions, "retryMaixRequestMessage", { timeout: 120000 })({ messageId });
  return result.data;
};

export const fetchAttorneyRequestEmails = async requestId => {
  const result = await httpsCallable(projectFunctions, "getAttorneyRequestEmails")({ requestId });
  return result.data;
};

export const sendAttorneyRequestEmail = async ({ requestId, messageId, subject, body }) => {
  const result = await httpsCallable(projectFunctions, "sendAttorneyRequestEmail")({ requestId, messageId, subject, body });
  return result.data?.message;
};

export const openAttorneyRequestSmsConversation = async requestId => {
  const safeRequestId = String(requestId ?? "").trim();

  if (!safeRequestId) {
    throw new Error("An attorney request ID is required.");
  }

  const openConversation = httpsCallable(
    projectFunctions,
    "openAttorneyRequestSmsConversation"
  );
  const result = await openConversation({ requestId: safeRequestId });
  const data = result.data ?? {};

  return {
    threadId: String(data.threadId ?? "").trim(),
    mobile: String(data.mobile ?? "").trim(),
    optOutStatus: String(data.optOutStatus ?? "active").trim()
  };
};

export const sendMaixTextMessage = async ({ threadId, text, contextType, contextId }) => {
  const safeThreadId = String(threadId ?? "").trim();
  const safeText = String(text ?? "").trim();

  if (!safeThreadId || !safeText) {
    throw new Error("A conversation and message are required.");
  }

  const sendMessage = httpsCallable(projectFunctions, "sendSmsMessage");
  const result = await sendMessage({
    threadId: safeThreadId,
    text: safeText,
    contextType: String(contextType ?? "maix_texts").trim(),
    contextId: String(contextId ?? "").trim()
  });

  return result.data?.message ?? null;
};

export const markMaixTextThreadRead = async threadId => {
  const safeThreadId = String(threadId ?? "").trim();

  if (!safeThreadId) { return; }

  const markRead = httpsCallable(projectFunctions, "markSmsThreadRead");
  await markRead({ threadId: safeThreadId });
};

export const setMaixTextConversationArchived = async ({ threadId, archived }) => {
  const safeThreadId = String(threadId ?? "").trim();

  if (!safeThreadId || typeof archived !== "boolean") {
    throw new Error("A Maix Text conversation and archive state are required.");
  }

  const setArchived = httpsCallable(projectFunctions, "setSmsConversationArchived");
  const result = await setArchived({ threadId: safeThreadId, archived });
  const fallbackStatus = archived ? "archived" : "active";

  return String(result.data?.conversationStatus ?? fallbackStatus);
};

export const deleteMaixTextConversation = async ({ threadId, confirmation }) => {
  const safeThreadId = String(threadId ?? "").trim();

  if (!safeThreadId || confirmation !== "CONFIRM") {
    throw new Error("Type CONFIRM to delete this conversation.");
  }

  const deleteConversation = httpsCallable(projectFunctions, "deleteSmsConversation");
  const result = await deleteConversation({
    threadId: safeThreadId,
    confirmation
  });

  return result.data?.deleted === true;
};

// MARK: - 4. Litigation Management

export const fetchNotificationTagCatalog = async () => {
  const snapshot = await getDocs(collection(db, "notificationTags"));

  return snapshot.docs
    .map(tagDocument => {
      const data = tagDocument.data();

      return {
        id: String(data.id ?? tagDocument.id).trim(),
        category: String(data.category ?? "").trim(),
        label: String(data.label ?? "").trim(),
        description: String(data.description ?? "").trim(),
        active: data.active === true,
        sortOrder: Number.isInteger(data.sortOrder) ? data.sortOrder : 0
      };
    })
    .filter(tag =>
      tag.id &&
      ["legal_topic", "audience"].includes(tag.category) &&
      tag.label
    )
    .sort((first, second) => {
      if (first.active !== second.active) { return first.active ? -1 : 1; }
      if (first.sortOrder !== second.sortOrder) {
        return first.sortOrder - second.sortOrder;
      }

      return first.label.localeCompare(second.label, undefined, {
        sensitivity: "base"
      });
    });
};

const litigationPublicationStatuses = new Set([
  "draft",
  "inReview",
  "published",
  "archived"
]);

const litigationQuestionCategories = new Set([
  "qualification",
  "personalInjury",
  "economicDamage"
]);

const litigationQuestionResponseTypes = new Set([
  "keyboard",
  "number",
  "yesNo"
]);

const litigationQuestionSupplementalOptions = new Set([
  "skip",
  "next",
  "notApplicable"
]);

const validateLitigationDataContract = (data, documentId) => {
  const errors = [];

  if (data.schemaVersion !== 1) { errors.push("schemaVersion"); }
  if (data.litigationId !== documentId) { errors.push("litigationId"); }
  if (!String(data.internalName ?? "").trim()) { errors.push("internalName"); }
  if (!String(data.publicActionName ?? "").trim()) { errors.push("publicActionName"); }
  if (!String(data.description ?? "").trim()) { errors.push("description"); }
  const hasInvalidSearchMetadata = value =>
    !Array.isArray(value ?? []) ||
    (value ?? []).some(entry =>
      typeof entry !== "string" || !entry.trim()
    );

  if (hasInvalidSearchMetadata(data.tags)) { errors.push("tags"); }
  if (hasInvalidSearchMetadata(data.searchTerms)) { errors.push("searchTerms"); }
  if (hasInvalidSearchMetadata(data.audienceTags)) { errors.push("audienceTags"); }
  if (hasInvalidSearchMetadata(data.audienceTagIds)) { errors.push("audienceTagIds"); }
  if (
    data.potentialExpectations !== null &&
    typeof data.potentialExpectations !== "string"
  ) {
    errors.push("potentialExpectations");
  }
  if (typeof data.imageUrl !== "string") { errors.push("imageUrl"); }
  if (!litigationPublicationStatuses.has(data.publicationStatus)) {
    errors.push("publicationStatus");
  }
  if (!Number.isInteger(data.submissionCount) || data.submissionCount < 0) {
    errors.push("submissionCount");
  }
  if (!Array.isArray(data.questions)) {
    errors.push("questions");
  }
  if (
    Array.isArray(data.questions) &&
    data.publicationStatus !== "draft" &&
    data.questions.length === 0
  ) {
    errors.push("questions for review or publication");
  }
  if (!data.createdAt?.toDate) { errors.push("createdAt"); }
  if (!data.updatedAt?.toDate) { errors.push("updatedAt"); }
  if (data.lastSubmissionAt != null && !data.lastSubmissionAt.toDate) {
    errors.push("lastSubmissionAt");
  }
  if (data.publishedAt != null && !data.publishedAt.toDate) {
    errors.push("publishedAt");
  }
  if (data.publicationStatus === "published" && data.publishedAt == null) {
    errors.push("publishedAt for published Litigation");
  }

  const invalidQuestion = Array.isArray(data.questions)
    ? data.questions.some(question => {
      return !String(question.id ?? "").trim() ||
        !litigationQuestionCategories.has(question.category) ||
        !String(question.prompt ?? "").trim() ||
        !litigationQuestionResponseTypes.has(question.responseType) ||
        !Array.isArray(question.supplementalOptions) ||
        question.supplementalOptions.some(
          option => !litigationQuestionSupplementalOptions.has(option)
        );
    })
    : true;

  if (invalidQuestion) { errors.push("question fields"); }

  const legacyFields = [
    "litId",
    "name",
    "expectations",
    "banner",
    "litLiveIo",
    "xSubmitted",
    "lastSubmitted",
    "requirements",
    "personalInjuries",
    "propertyDamages"
  ];

  const presentLegacyFields = legacyFields.filter(field => field in data);

  if (presentLegacyFields.length > 0) {
    errors.push(`legacy fields: ${presentLegacyFields.join(", ")}`);
  }

  if (errors.length > 0) {
    throw new Error(
      `Litigation ${documentId} does not match LitigationDataModel: ${errors.join(", ")}`
    );
  }
};

export const fetchLitigations = async () => {
  try {
    const litigationsRef = collection(db, "admin", "lits", "feed");
    const litigationsQuery = query(litigationsRef, orderBy("createdAt", "desc"));
    const snapshot = await getDocs(litigationsQuery);

    return snapshot.docs.map(litigationDocument => {
      const data = litigationDocument.data();

      validateLitigationDataContract(data, litigationDocument.id);

      return {
        id: litigationDocument.id,
        schemaVersion: data.schemaVersion,
        litigationId: data.litigationId,
        internalName: data.internalName,
        publicActionName: data.publicActionName,
        description: data.description,
        tags: normalizeSearchMetadata(data.tags),
        searchTerms: normalizeSearchMetadata(data.searchTerms),
        audienceTags: normalizeSearchMetadata(data.audienceTags),
        audienceTagIds: normalizeNotificationTagIds(data.audienceTagIds),
        potentialExpectations: data.potentialExpectations ?? null,
        imageUrl: data.imageUrl,
        questions: data.questions,
        publicationStatus: data.publicationStatus,
        submissionCount: data.submissionCount,
        lastSubmissionAt: data.lastSubmissionAt ?? null,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        publishedAt: data.publishedAt ?? null
      };
    });
  } catch (error) {
    console.error("❌ Error fetching litigations:", error);

    return [];
  }
};

export const fetchLitigationSubmissions = async () => {
  try {
    const [submissionSnapshot, litigations] = await Promise.all([
      getDocs(collectionGroup(db, "submissionEvents")),
      fetchLitigations()
    ]);
    const litigationNames = new Map(
      litigations.map(litigation => [litigation.litigationId, litigation])
    );

    return submissionSnapshot.docs
      .map(submissionDocument => {
        const data = submissionDocument.data();
        const litigation = litigationNames.get(data.litigationId);

        return {
          id: `${data.litigationId}:${data.clientId}`,
          clientId: String(data.clientId ?? submissionDocument.id),
          litigationId: String(data.litigationId ?? ""),
          publicActionName: String(
            data.publicActionName ??
            litigation?.publicActionName ??
            "Litigation Submission"
          ),
          internalName: String(
            data.internalName ?? litigation?.internalName ?? ""
          ),
          answerCount: Number.isInteger(data.answerCount)
            ? data.answerCount
            : null,
          applicationSessionPath: String(data.applicationSessionPath ?? ""),
          status: String(data.status ?? "new"),
          clientStatus: String(
            data.clientStatus ??
            clientStatusForLitigationSubmissionWorkflow(data.status ?? "new")
          ),
          statusUpdatedById: String(data.statusUpdatedById ?? ""),
          statusUpdatedByName: String(data.statusUpdatedByName ?? ""),
          completedAt: data.completedAt ?? null,
          openedAt: data.openedAt ?? null,
          editedOnDate: data.editedOnDate ?? null,
          needsReview: typeof data.needsReview === "boolean"
            ? data.needsReview
            : null,
          lastReviewedDate: data.lastReviewedDate ?? null,
          lastReviewedById: String(data.lastReviewedById ?? ""),
          lastReviewedByName: String(data.lastReviewedByName ?? ""),
          reviewStateUpdatedAt: data.reviewStateUpdatedAt ?? null,
          reviewStateUpdatedById: String(data.reviewStateUpdatedById ?? ""),
          reviewStateUpdatedByName: String(data.reviewStateUpdatedByName ?? ""),
          updatedAt: data.updatedAt ?? data.completedAt ?? null
        };
      })
      .filter(submission => submission.litigationId && submission.completedAt)
      .sort((first, second) => {
        const firstTime = first.completedAt?.toMillis?.() ?? 0;
        const secondTime = second.completedAt?.toMillis?.() ?? 0;

        return secondTime - firstTime;
      });
  } catch (error) {
    console.error("❌ Unable to fetch Litigation submissions:", error);

    throw error;
  }
};

const litigationSubmissionRef = ({ litigationId, clientId }) => {
  const safeLitigationId = String(litigationId ?? "").trim();
  const safeClientId = String(clientId ?? "").trim();

  if (!safeLitigationId || !safeClientId) {
    throw new Error("A Litigation and App Client are required.");
  }

  return doc(
    db,
    "admin",
    "lits",
    "feed",
    safeLitigationId,
    "submissionEvents",
    safeClientId
  );
};

export const fetchLitigationSubmissionClientInfo = async submission => {
  const litigationId = String(submission?.litigationId ?? "").trim();
  const clientId = String(submission?.clientId ?? "").trim();

  if (!litigationId || !clientId) {
    throw new Error("A Litigation and App Client are required.");
  }

  const fetchClientInfo = httpsCallable(
    projectFunctions,
    "getLitigationSubmissionClientInfo"
  );
  const result = await fetchClientInfo({ litigationId, clientId });
  const data = result.data ?? {};

  return {
    name: String(data.name ?? "").trim(),
    jurisdiction: String(data.jurisdiction ?? "").trim()
  };
};

export const fetchLitigationSubmissionSession = async submission => {
  try {
    const safeLitigationId = String(submission?.litigationId ?? "").trim();
    const safeClientId = String(submission?.clientId ?? "").trim();

    if (!safeLitigationId || !safeClientId) {
      throw new Error("A Litigation and App Client are required.");
    }

    const sessionSnapshot = await getDoc(
      doc(db, "clients", safeClientId, "subs", safeLitigationId)
    );

    if (!sessionSnapshot.exists()) {
      throw new Error("The submitted application session was not found.");
    }

    return {
      id: sessionSnapshot.id,
      ...sessionSnapshot.data()
    };
  } catch (error) {
    console.error("❌ Unable to fetch the Litigation application session:", error);

    throw error;
  }
};

export const fetchLitigationSubmissionNotes = async submission => {
  const notesRef = collection(litigationSubmissionRef(submission), "notes");
  const snapshot = await getDocs(query(notesRef, orderBy("createdAt", "desc")));

  return snapshot.docs.map(noteDocument => ({
    id: noteDocument.id,
    ...noteDocument.data()
  }));
};

export const addLitigationSubmissionNote = async (
  submission,
  { text, authorId, authorName }
) => {
  const trimmedText = String(text ?? "").trim();

  if (!trimmedText) {
    throw new Error("An internal note cannot be empty.");
  }

  const noteRef = doc(collection(litigationSubmissionRef(submission), "notes"));
  const note = {
    id: noteRef.id,
    litigationId: String(submission.litigationId),
    clientId: String(submission.clientId),
    text: trimmedText,
    authorId: String(authorId ?? ""),
    authorName: String(authorName ?? ""),
    createdAt: serverTimestamp()
  };

  await setDoc(noteRef, note);

  return {
    ...note,
    createdAt: Timestamp.now()
  };
};

export const fetchLitigationSubmissionMessages = async submission => {
  const messagesRef = collection(litigationSubmissionRef(submission), "messages");
  const snapshot = await getDocs(query(messagesRef, orderBy("createdAt", "desc")));

  return snapshot.docs
    .map(messageDocument => ({
      id: messageDocument.id,
      ...messageDocument.data()
    }))
    .filter(message =>
      message.intendedForClient === true && message.visibility === "client"
    );
};

export const addLitigationSubmissionMessage = async (
  submission,
  { text, authorId, authorName }
) => {
  const trimmedText = String(text ?? "").trim();

  if (!trimmedText) {
    throw new Error("A submission message cannot be empty.");
  }

  const messageRef = doc(
    collection(litigationSubmissionRef(submission), "messages")
  );
  const message = {
    schemaVersion: 1,
    id: messageRef.id,
    litigationId: String(submission.litigationId),
    clientId: String(submission.clientId),
    text: trimmedText,
    authorId: String(authorId ?? ""),
    authorName: String(authorName ?? ""),
    intendedForClient: true,
    visibility: "client",
    senderRole: "firm",
    deliveryStatus: "saved",
    createdAt: serverTimestamp()
  };

  await setDoc(messageRef, message);

  return {
    ...message,
    createdAt: Timestamp.now()
  };
};

export const markLitigationSubmissionReviewed = async (
  submission,
  { adminId, adminName }
) => {
  const safeAdminId = String(adminId ?? "").trim();
  const safeAdminName = String(adminName ?? "").trim();

  if (!safeAdminId) {
    throw new Error("An authenticated administrator is required.");
  }

  const submissionRef = litigationSubmissionRef(submission);

  await updateDoc(submissionRef, {
    needsReview: false,
    lastReviewedDate: serverTimestamp(),
    lastReviewedById: safeAdminId,
    lastReviewedByName: safeAdminName,
    reviewStateUpdatedAt: serverTimestamp(),
    reviewStateUpdatedById: safeAdminId,
    reviewStateUpdatedByName: safeAdminName
  });

  const reviewedSnapshot = await getDoc(submissionRef);
  const reviewedData = reviewedSnapshot.data() ?? {};

  return {
    ...submission,
    needsReview: reviewedData.needsReview ?? false,
    lastReviewedDate: reviewedData.lastReviewedDate ?? Timestamp.now(),
    lastReviewedById: String(reviewedData.lastReviewedById ?? safeAdminId),
    lastReviewedByName: String(reviewedData.lastReviewedByName ?? safeAdminName),
    reviewStateUpdatedAt: reviewedData.reviewStateUpdatedAt ?? Timestamp.now(),
    reviewStateUpdatedById: String(reviewedData.reviewStateUpdatedById ?? safeAdminId),
    reviewStateUpdatedByName: String(reviewedData.reviewStateUpdatedByName ?? safeAdminName)
  };
};

export const markLitigationSubmissionNeedsReview = async (
  submission,
  { adminId, adminName }
) => {
  const safeAdminId = String(adminId ?? "").trim();
  const safeAdminName = String(adminName ?? "").trim();

  if (!safeAdminId) {
    throw new Error("An authenticated administrator is required.");
  }

  const submissionRef = litigationSubmissionRef(submission);

  await updateDoc(submissionRef, {
    needsReview: true,
    reviewStateUpdatedAt: serverTimestamp(),
    reviewStateUpdatedById: safeAdminId,
    reviewStateUpdatedByName: safeAdminName
  });

  const reviewSnapshot = await getDoc(submissionRef);
  const reviewData = reviewSnapshot.data() ?? {};

  return {
    ...submission,
    needsReview: reviewData.needsReview ?? true,
    reviewStateUpdatedAt: reviewData.reviewStateUpdatedAt ?? Timestamp.now(),
    reviewStateUpdatedById: String(reviewData.reviewStateUpdatedById ?? safeAdminId),
    reviewStateUpdatedByName: String(reviewData.reviewStateUpdatedByName ?? safeAdminName)
  };
};

export const updateLitigationSubmissionStatus = async (
  submission,
  status,
  { adminId, adminName }
) => {
  const allowedStatuses = new Set(litigationSubmissionWorkflowStatusValues);

  if (!allowedStatuses.has(status)) {
    throw new Error("The selected submission status is invalid.");
  }

  const safeAdminId = String(adminId ?? "").trim();
  const safeAdminName = String(adminName ?? "").trim();

  if (!safeAdminId) {
    throw new Error("An authenticated administrator is required.");
  }

  const submissionRef = litigationSubmissionRef(submission);
  const historyRef = doc(collection(submissionRef, "statusHistory"));
  const clientStatus = clientStatusForLitigationSubmissionWorkflow(status);
  const batch = writeBatch(db);

  batch.update(submissionRef, {
    status,
    clientStatus,
    statusUpdatedById: safeAdminId,
    statusUpdatedByName: safeAdminName,
    updatedAt: serverTimestamp()
  });

  batch.set(historyRef, {
    id: historyRef.id,
    litigationId: String(submission.litigationId),
    clientId: String(submission.clientId),
    fromStatus: String(submission.status ?? "new"),
    toStatus: status,
    clientStatus,
    changedById: safeAdminId,
    changedByName: safeAdminName,
    changedAt: serverTimestamp()
  });

  await batch.commit();

  return {
    ...submission,
    status,
    clientStatus,
    statusUpdatedById: safeAdminId,
    statusUpdatedByName: safeAdminName,
    updatedAt: Timestamp.now()
  };
};

export const fetchSignedLitigationContractPDF = async submission => {
  const clientId = String(submission?.clientId ?? "").trim();
  const litigationId = String(submission?.litigationId ?? "").trim();

  if (!clientId || !litigationId) {
    throw new Error("A Litigation and App Client are required.");
  }

  const fetchSignedContract = httpsCallable(
    projectFunctions,
    "getSignedLitigationContractPDF"
  );
  const result = await fetchSignedContract({ clientId, litigationId });
  const pdfBase64 = String(result.data?.pdfBase64 ?? "").trim();

  if (!pdfBase64) {
    throw new Error("The signed contract PDF was not returned.");
  }

  const binary = window.atob(pdfBase64);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return {
    url: URL.createObjectURL(new Blob([bytes], { type: "application/pdf" })),
    filename: String(result.data?.filename ?? "signed-contract.pdf"),
    sha256: String(result.data?.sha256 ?? "")
  };
};

export const saveLitigation = async (litigationData) => {
  try {
    const feedRef = collection(db, "admin", "lits", "feed");

    /*
     Create the reference first so its Firestore document ID
     can also be stored as the permanent litigationId.
    */
    const litigationRef = doc(feedRef);
    const litigationId = litigationRef.id;
    const requestedPublicationStatus = String(
      litigationData.publicationStatus ?? "draft"
    );

    const publicationStatus = litigationPublicationStatuses.has(
      requestedPublicationStatus
    )
      ? requestedPublicationStatus
      : "draft";

    const isPublished = publicationStatus === "published";

    await setDoc(litigationRef, {
      schemaVersion: 1,
      litigationId,
      internalName: String(litigationData.internalName ?? "").trim(),
      publicActionName: String(litigationData.publicActionName ?? "").trim(),
      description: String(litigationData.description ?? "").trim(),
      tags: normalizeSearchMetadata(litigationData.tags),
      searchTerms: normalizeSearchMetadata(litigationData.searchTerms),
      audienceTags: normalizeSearchMetadata(litigationData.audienceTags),
      audienceTagIds: normalizeNotificationTagIds(litigationData.audienceTagIds),
      potentialExpectations:
        String(litigationData.potentialExpectations ?? "").trim() || null,
      imageUrl: String(litigationData.imageUrl ?? "").trim(),
      questions: Array.isArray(litigationData.questions)
        ? litigationData.questions
        : [],
      publicationStatus,
      submissionCount: 0,
      lastSubmissionAt: null,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      publishedAt: isPublished ? serverTimestamp() : null
    });

    const createdSnapshot = await getDoc(litigationRef);

    validateLitigationDataContract(
      createdSnapshot.data() ?? {},
      litigationId
    );

    console.log("✅ Litigation saved with ID:", litigationId);

    const adminLitsRef = doc(db, "admin", "lits");

    await updateDoc(adminLitsRef, {
      litcount: increment(1),
      lastUpdated: serverTimestamp()
    });

    console.log("✅ Litcount updated and timestamp set.");

    return litigationId;
  } catch (error) {
    console.error("❌ Error saving litigation:", error);

    return null;
  }
};

export const updateLitigation = async (litigationId, litigationData) => {
  try {
    if (!litigationId) {
      throw new Error("A litigation ID is required.");
    }

    const litigationRef = doc(db, "admin", "lits", "feed", litigationId);
    const litigationSnapshot = await getDoc(litigationRef);

    if (!litigationSnapshot.exists()) {
      throw new Error("The Litigation does not exist.");
    }

    const existingData = litigationSnapshot.data();

    validateLitigationDataContract(existingData, litigationId);

    const requestedPublicationStatus = String(
      litigationData.publicationStatus ?? existingData.publicationStatus
    );

    const publicationStatus = litigationPublicationStatuses.has(
      requestedPublicationStatus
    )
      ? requestedPublicationStatus
      : existingData.publicationStatus;

    const updates = {
      schemaVersion: existingData.schemaVersion,
      litigationId,
      internalName: String(litigationData.internalName ?? "").trim(),
      publicActionName: String(litigationData.publicActionName ?? "").trim(),
      description: String(litigationData.description ?? "").trim(),
      tags: normalizeSearchMetadata(
        litigationData.tags ?? existingData.tags
      ),
      searchTerms: normalizeSearchMetadata(
        litigationData.searchTerms ?? existingData.searchTerms
      ),
      audienceTags: normalizeSearchMetadata(
        litigationData.audienceTags ?? existingData.audienceTags
      ),
      audienceTagIds: normalizeNotificationTagIds(
        litigationData.audienceTagIds ?? existingData.audienceTagIds
      ),
      potentialExpectations:
        String(litigationData.potentialExpectations ?? "").trim() || null,
      questions: Array.isArray(litigationData.questions)
        ? litigationData.questions
        : existingData.questions,
      publicationStatus,
      updatedAt: serverTimestamp()
    };

    if (
      publicationStatus === "published" &&
      existingData.publicationStatus !== "published"
    ) {
      updates.publishedAt = serverTimestamp();
    }

    const proposedData = {
      ...existingData,
      ...updates,
      updatedAt: Timestamp.now()
    };

    if (updates.publishedAt) {
      proposedData.publishedAt = Timestamp.now();
    }

    validateLitigationDataContract(proposedData, litigationId);

    await updateDoc(litigationRef, updates);

    const updatedSnapshot = await getDoc(litigationRef);

    validateLitigationDataContract(
      updatedSnapshot.data() ?? {},
      litigationId
    );

    console.log("✅ Litigation updated:", litigationId);

    return true;
  } catch (error) {
    console.error("❌ Error updating litigation:", error);

    return false;
  }
};

export const updateLitigationBanner = async (litigationId, bannerUrl) => {
  try {
    const litigationRef = doc(db, `admin/lits/feed/${litigationId}`);
    await updateDoc(litigationRef, {
      imageUrl: bannerUrl,
      updatedAt: serverTimestamp()
    });
    console.log("✅ Firestore updated with image:", bannerUrl);
    return true;
  } catch (error) {
    console.error("❌ Error updating Firestore banner:", error);
    return false;
  }
};

export const deleteLitigation = async litigationId => {
  try {
    if (!litigationId) {
      throw new Error("A litigation ID is required.");
    }

    const litigationRef = doc(db, "admin", "lits", "feed", litigationId);
    const adminLitsRef = doc(db, "admin", "lits");

    await runTransaction(db, async transaction => {
      const snapshot = await transaction.get(litigationRef);

      if (!snapshot.exists()) {
        throw new Error("The Litigation does not exist.");
      }

      transaction.delete(litigationRef);
      transaction.set(
        adminLitsRef,
        {
          litcount: increment(-1),
          lastUpdated: serverTimestamp()
        },
        { merge: true }
      );
    });

    console.log("✅ Litigation removed:", litigationId);
    return true;
  } catch (error) {
    console.error("❌ Error removing litigation:", error);
    return false;
  }
};

const contentEditLeaseDuration = 90 * 1000;

const contentEditLeaseRef = (contentCollection, contentId) =>
  doc(
    db,
    "admin",
    contentCollection,
    "feed",
    contentId,
    "editSessions",
    "active"
  );

const normalizeContentEditLease = snapshot => {
  if (!snapshot.exists()) { return null; }

  const data = snapshot.data();

  return {
    adminId: String(data.adminId ?? ""),
    adminName: String(data.adminName ?? "Admin"),
    expiresAt: data.expiresAt ?? null
  };
};

const listenToContentEditLease = (
  contentCollection,
  contentId,
  contentLabel,
  callback
) => {
  if (!contentId) {
    callback(null);
    return () => {};
  }

  return onSnapshot(
    contentEditLeaseRef(contentCollection, contentId),
    snapshot => callback(normalizeContentEditLease(snapshot)),
    error => {
      console.error(`Unable to monitor the ${contentLabel} editor:`, error);
      callback(null, error);
    }
  );
};

const claimContentEditLease = async (
  contentCollection,
  contentId,
  contentLabel,
  adminName
) => {
  const adminId = projectAuth.currentUser?.uid;

  if (!contentId || !adminId) {
    throw new Error(
      `An authenticated admin and ${contentLabel} ID are required.`
    );
  }

  const leaseRef = contentEditLeaseRef(contentCollection, contentId);

  return runTransaction(db, async transaction => {
    const snapshot = await transaction.get(leaseRef);
    const currentLease = normalizeContentEditLease(snapshot);
    const expiresAt = currentLease?.expiresAt?.toMillis?.() ?? 0;
    const isAvailable =
      !currentLease ||
      currentLease.adminId === adminId ||
      expiresAt <= Date.now();

    if (!isAvailable) {
      return {
        acquired: false,
        lease: currentLease
      };
    }

    const lease = {
      adminId,
      adminName: String(adminName ?? "").trim() || "Admin",
      heartbeatAt: serverTimestamp(),
      expiresAt: Timestamp.fromMillis(Date.now() + contentEditLeaseDuration)
    };

    transaction.set(leaseRef, lease);

    return {
      acquired: true,
      lease: {
        ...lease,
        heartbeatAt: Timestamp.now()
      }
    };
  });
};

const releaseContentEditLease = async (
  contentCollection,
  contentId
) => {
  const adminId = projectAuth.currentUser?.uid;

  if (!contentId || !adminId) { return; }

  const leaseRef = contentEditLeaseRef(contentCollection, contentId);

  await runTransaction(db, async transaction => {
    const snapshot = await transaction.get(leaseRef);

    if (
      snapshot.exists() &&
      snapshot.data().adminId === adminId
    ) {
      transaction.delete(leaseRef);
    }
  });
};

export const listenToLitigationEditLease = (litigationId, callback) =>
  listenToContentEditLease(
    "lits",
    litigationId,
    "litigation",
    callback
  );

export const claimLitigationEditLease = (litigationId, adminName) =>
  claimContentEditLease("lits", litigationId, "litigation", adminName);

export const releaseLitigationEditLease = litigationId =>
  releaseContentEditLease("lits", litigationId);

export const listenToWatchlistEditLease = (watchlistId, callback) =>
  listenToContentEditLease(
    "watchlists",
    watchlistId,
    "Watchlist",
    callback
  );

export const claimWatchlistEditLease = (watchlistId, adminName) =>
  claimContentEditLease("watchlists", watchlistId, "Watchlist", adminName);

export const releaseWatchlistEditLease = watchlistId =>
  releaseContentEditLease("watchlists", watchlistId);

// MARK: - 5. CourtListener Updates

const courtListenerMonitorsRef = () =>
  collection(db, "admin", "lits", "monitors");

const assertCurrentAdmin = adminId => {
  const currentAdminId = projectAuth.currentUser?.uid;

  if (!currentAdminId || currentAdminId !== String(adminId ?? "").trim()) {
    throw new Error("The authenticated administrator is required.");
  }

  return currentAdminId;
};

const assertUniqueCourtListenerDocket = async (docketId, excludedMonitorId = "") => {
  const duplicateQuery = query(
    courtListenerMonitorsRef(),
    where("courtListenerDocketId", "==", docketId),
    limit(2)
  );
  const duplicateSnapshot = await getDocs(duplicateQuery);
  const duplicate = duplicateSnapshot.docs.find(
    monitorDocument => monitorDocument.id !== excludedMonitorId
  );

  if (duplicate) {
    throw new Error("That CourtListener docket is already monitored.");
  }
};

export const fetchCourtListenerMonitors = async () => {
  const monitorsQuery = query(
    courtListenerMonitorsRef(),
    orderBy("caseName", "asc")
  );
  const snapshot = await getDocs(monitorsQuery);

  return snapshot.docs.map(monitorDocument => ({
    id: monitorDocument.id,
    ...monitorDocument.data()
  }));
};

export const saveCourtListenerMonitor = async (monitor, adminId) => {
  const safeAdminId = assertCurrentAdmin(adminId);
  const normalizedMonitor = normalizeCourtListenerMonitor(monitor);
  const litigationSnapshot = await getDoc(
    doc(db, "admin", "lits", "feed", normalizedMonitor.litigationId)
  );

  if (!litigationSnapshot.exists()) {
    throw new Error("The selected Litigation does not exist.");
  }

  await assertUniqueCourtListenerDocket(normalizedMonitor.courtListenerDocketId);

  const monitorRef = doc(courtListenerMonitorsRef());

  await setDoc(monitorRef, {
    ...normalizedMonitor,
    monitorId: monitorRef.id,
    createdAt: serverTimestamp(),
    createdBy: safeAdminId,
    updatedAt: serverTimestamp(),
    updatedBy: safeAdminId,
    lastEventAt: null,
    lastWebhookEventId: null
  });

  return monitorRef.id;
};

export const updateCourtListenerMonitor = async (monitorId, monitor, adminId) => {
  const safeMonitorId = String(monitorId ?? "").trim();
  const safeAdminId = assertCurrentAdmin(adminId);

  if (!safeMonitorId) {
    throw new Error("A CourtListener monitor is required.");
  }

  const monitorRef = doc(db, "admin", "lits", "monitors", safeMonitorId);
  const monitorSnapshot = await getDoc(monitorRef);

  if (!monitorSnapshot.exists()) {
    throw new Error("The CourtListener monitor does not exist.");
  }

  const existingMonitor = monitorSnapshot.data();
  const normalizedMonitor = normalizeCourtListenerMonitor({
    ...existingMonitor,
    ...monitor,
    litigationId: existingMonitor.litigationId
  });

  await assertUniqueCourtListenerDocket(
    normalizedMonitor.courtListenerDocketId,
    safeMonitorId
  );

  await updateDoc(monitorRef, {
    mdlNumber: normalizedMonitor.mdlNumber,
    courtListenerDocketId: normalizedMonitor.courtListenerDocketId,
    courtListenerAlertId: normalizedMonitor.courtListenerAlertId,
    courtId: normalizedMonitor.courtId,
    docketNumber: normalizedMonitor.docketNumber,
    caseName: normalizedMonitor.caseName,
    enabled: normalizedMonitor.enabled,
    updatedAt: serverTimestamp(),
    updatedBy: safeAdminId
  });
};

export const fetchCourtListenerUpdateEvents = async (maxResults = 100) => {
  const safeLimit = Math.min(Math.max(Number(maxResults) || 100, 1), 200);
  const eventsQuery = query(
    collection(db, "admin", "lits", "updateEvents"),
    orderBy("receivedAt", "desc"),
    limit(safeLimit)
  );
  const snapshot = await getDocs(eventsQuery);

  return snapshot.docs.map(eventDocument => ({
    id: eventDocument.id,
    ...eventDocument.data()
  }));
};

// MARK: - 6. Watchlist Management

const watchlistPublicationStatuses = new Set(
  watchlistPublicationStatusValues
);

const watchlistQuestionResponseTypes = new Set(
  watchlistQuestionResponseTypeValues
);

const normalizeWatchlistDataContract = (data, documentId) => {
  const publicationStatus = normalizeWatchlistPublicationStatus(
    data.publicationStatus,
    data.isPublished
  );

  return {
    schemaVersion: data.schemaVersion ?? 1,
    watchlistId: data.watchlistId ?? documentId,
    name: data.name ?? "",
    description: data.description ?? "",
    tags: normalizeSearchMetadata(data.tags),
    searchTerms: normalizeSearchMetadata(data.searchTerms),
    explainerTitle: data.explainerTitle ?? "",
    explainerDescription: data.explainerDescription ?? "",
    bannerUrl: data.bannerUrl ?? "",
    publicationStatus,
    isPublished: isPublishedWatchlistStatus(publicationStatus),
    questions: Array.isArray(data.questions) ? data.questions : [],
    submissionCount: data.submissionCount ?? 0,
    lastSubmissionAt: data.lastSubmissionAt ?? null,
    createdAt: data.createdAt ?? null,
    updatedAt: data.updatedAt ?? null,
    publishedAt: data.publishedAt ?? (
      isPublishedWatchlistStatus(publicationStatus) ?
        data.updatedAt ?? data.createdAt ?? null :
        null
    )
  };
};

const validateWatchlistDataContract = (data, documentId) => {
  const errors = [];
  const requiresPublishableContent = data.publicationStatus !== "draft";

  if (data.schemaVersion !== 1) { errors.push("schemaVersion"); }
  if (data.watchlistId !== documentId) { errors.push("watchlistId"); }
  if (typeof data.name !== "string") { errors.push("name"); }
  if (typeof data.description !== "string") { errors.push("description"); }
  if (!Array.isArray(data.tags)) { errors.push("tags"); }
  if (
    Array.isArray(data.tags) &&
    data.tags.some(entry => typeof entry !== "string" || !entry.trim())
  ) {
    errors.push("tag values");
  }
  if (!Array.isArray(data.searchTerms)) { errors.push("searchTerms"); }
  if (
    Array.isArray(data.searchTerms) &&
    data.searchTerms.some(entry => typeof entry !== "string" || !entry.trim())
  ) {
    errors.push("searchTerm values");
  }
  if (typeof data.explainerTitle !== "string") { errors.push("explainerTitle"); }
  if (typeof data.explainerDescription !== "string") {
    errors.push("explainerDescription");
  }
  if (typeof data.bannerUrl !== "string") { errors.push("bannerUrl"); }
  if (!watchlistPublicationStatuses.has(data.publicationStatus)) {
    errors.push("publicationStatus");
  }
  if (
    typeof data.isPublished !== "boolean" ||
    data.isPublished !== isPublishedWatchlistStatus(data.publicationStatus)
  ) {
    errors.push("isPublished compatibility field");
  }
  if (!Array.isArray(data.questions)) { errors.push("questions"); }
  if (!Number.isInteger(data.submissionCount) || data.submissionCount < 0) {
    errors.push("submissionCount");
  }
  if (!data.createdAt?.toDate) { errors.push("createdAt"); }
  if (!data.updatedAt?.toDate) { errors.push("updatedAt"); }
  if (data.lastSubmissionAt != null && !data.lastSubmissionAt.toDate) {
    errors.push("lastSubmissionAt");
  }
  if (data.publishedAt != null && !data.publishedAt.toDate) {
    errors.push("publishedAt");
  }
  if (
    data.publicationStatus === "published" &&
    data.publishedAt == null
  ) {
    errors.push("publishedAt for published Watchlist");
  }

  if (requiresPublishableContent) {
    if (!data.name.trim()) { errors.push("name for review or publication"); }
    if (!data.description.trim()) {
      errors.push("description for review or publication");
    }
    if (!data.explainerTitle.trim()) {
      errors.push("explainerTitle for review or publication");
    }
    if (!data.explainerDescription.trim()) {
      errors.push("explainerDescription for review or publication");
    }
    if (!data.bannerUrl.trim()) {
      errors.push("bannerUrl for review or publication");
    }
    if (!Array.isArray(data.questions) || data.questions.length === 0) {
      errors.push("questions for review or publication");
    }
  }

  const questionIds = new Set();
  const invalidQuestion = Array.isArray(data.questions)
    ? data.questions.some((question, index) => {
      const id = String(question.id ?? "").trim();
      const responseOptions = question.responseOptions;
      const hasDuplicateId = questionIds.has(id);

      if (id) { questionIds.add(id); }

      return !id ||
        hasDuplicateId ||
        question.order !== index ||
        !String(question.question ?? "").trim() ||
        !watchlistQuestionResponseTypes.has(question.responseType) ||
        !Array.isArray(responseOptions) ||
        responseOptions.some(option => !String(option ?? "").trim()) ||
        (
          question.responseType === "Options" &&
          responseOptions.length === 0
        ) ||
        (
          question.responseType !== "Options" &&
          responseOptions.length !== 0
        ) ||
        typeof question.isRequired !== "boolean";
    })
    : true;

  if (invalidQuestion) { errors.push("question fields"); }

  if (errors.length > 0) {
    throw new Error(
      `Watchlist ${documentId} does not match WatchlistDataModel: ${errors.join(", ")}`
    );
  }
};

export const fetchWatchlists = async () => {
  try {
    const watchlistsRef = collection(db, "admin", "watchlists", "feed");
    const watchlistsQuery = query(watchlistsRef, orderBy("createdAt", "desc"));
    const snapshot = await getDocs(watchlistsQuery);

    return snapshot.docs.map(watchlistDocument => {
      const data = normalizeWatchlistDataContract(
        watchlistDocument.data(),
        watchlistDocument.id
      );

      validateWatchlistDataContract(data, watchlistDocument.id);

      return {
        id: watchlistDocument.id,
        ...data
      };
    });
  } catch (error) {
    console.error("❌ Error fetching watchlists:", error);

    throw error;
  }
};

export const fetchWatchTrendReports = async () => {
  try {
    const [watchEventSnapshot, watchlists] = await Promise.all([
      getDocs(collectionGroup(db, "watchEvents")),
      fetchWatchlists()
    ]);
    const watchlistDefinitions = new Map(
      watchlists.map(watchlist => [watchlist.watchlistId, watchlist])
    );

    return watchEventSnapshot.docs
      .map(watchEventDocument => {
        const data = watchEventDocument.data();
        const watchlist = watchlistDefinitions.get(data.watchlistId);
        const eventId = watchEventDocument.id;

        return {
          id: eventId,
          eventId,
          clientId: String(data.clientId ?? watchEventDocument.id),
          sessionId: String(data.sessionId ?? ""),
          watchlistId: String(data.watchlistId ?? ""),
          name: String(data.name ?? watchlist?.name ?? "Watchlist Report"),
          description: String(watchlist?.description ?? ""),
          explainerTitle: String(watchlist?.explainerTitle ?? ""),
          explainerDescription: String(watchlist?.explainerDescription ?? ""),
          answerCount: Number.isInteger(data.answerCount)
            ? data.answerCount
            : null,
          watchlistSessionPath: String(data.watchlistSessionPath ?? ""),
          status: String(data.status ?? "completed"),
          statusUpdatedById: String(data.statusUpdatedById ?? ""),
          statusUpdatedByName: String(data.statusUpdatedByName ?? ""),
          completedAt: data.completedAt ?? null,
          editedOnDate: data.editedOnDate ?? null,
          needsReview: typeof data.needsReview === "boolean"
            ? data.needsReview
            : null,
          lastReviewedDate: data.lastReviewedDate ?? null,
          lastReviewedById: String(data.lastReviewedById ?? ""),
          lastReviewedByName: String(data.lastReviewedByName ?? ""),
          reviewStateUpdatedAt: data.reviewStateUpdatedAt ?? null,
          reviewStateUpdatedById: String(data.reviewStateUpdatedById ?? ""),
          reviewStateUpdatedByName: String(data.reviewStateUpdatedByName ?? ""),
          updatedAt: data.updatedAt ?? data.completedAt ?? null
        };
      })
      .filter(report => report.watchlistId && report.completedAt)
      .sort((first, second) => {
        const firstTime = first.completedAt?.toMillis?.() ?? 0;
        const secondTime = second.completedAt?.toMillis?.() ?? 0;

        return secondTime - firstTime;
      });
  } catch (error) {
    console.error("❌ Unable to fetch Watchlist trend reports:", error);
    throw error;
  }
};

const watchTrendRef = ({ watchlistId, eventId, id, clientId }) => {
  const safeWatchlistId = String(watchlistId ?? "").trim();
  const safeEventId = String(eventId ?? id ?? clientId ?? "").trim();

  if (!safeWatchlistId || !safeEventId) {
    throw new Error("A Watchlist and Watchlist report are required.");
  }

  return doc(
    db,
    "admin",
    "watchlists",
    "feed",
    safeWatchlistId,
    "watchEvents",
    safeEventId
  );
};

export const fetchWatchTrendSession = async report => {
  const safeWatchlistId = String(report?.watchlistId ?? "").trim();
  const safeClientId = String(report?.clientId ?? "").trim();
  const safeSessionId = String(report?.sessionId ?? "").trim();
  const safeSessionPath = String(report?.watchlistSessionPath ?? "").trim();

  if (!safeWatchlistId || !safeClientId) {
    throw new Error("A Watchlist and App Client are required.");
  }

  const expectedPrefix = `clients/${safeClientId}/watchlistSessions/`;

  if (safeSessionPath && !safeSessionPath.startsWith(expectedPrefix)) {
    throw new Error("The Watchlist session path is invalid.");
  }

  const sessionRef = safeSessionPath
    ? doc(db, safeSessionPath)
    : doc(
      db,
      "clients",
      safeClientId,
      "watchlistSessions",
      safeSessionId || safeWatchlistId
    );
  const sessionSnapshot = await getDoc(sessionRef);

  if (!sessionSnapshot.exists()) {
    throw new Error("The completed Watchlist session was not found.");
  }

  return {
    id: sessionSnapshot.id,
    ...sessionSnapshot.data()
  };
};

export const fetchWatchTrendNotes = async report => {
  const notesRef = collection(watchTrendRef(report), "notes");
  const snapshot = await getDocs(query(notesRef, orderBy("createdAt", "desc")));

  return snapshot.docs.map(noteDocument => ({
    id: noteDocument.id,
    ...noteDocument.data()
  }));
};

export const addWatchTrendNote = async (
  report,
  { text, authorId, authorName }
) => {
  const trimmedText = String(text ?? "").trim();

  if (!trimmedText) {
    throw new Error("An internal note cannot be empty.");
  }

  const noteRef = doc(collection(watchTrendRef(report), "notes"));
  const note = {
    id: noteRef.id,
    watchlistId: String(report.watchlistId),
    clientId: String(report.clientId),
    text: trimmedText,
    authorId: String(authorId ?? ""),
    authorName: String(authorName ?? ""),
    createdAt: serverTimestamp()
  };

  await setDoc(noteRef, note);

  return {
    ...note,
    createdAt: Timestamp.now()
  };
};

export const fetchWatchTrendMessages = async report => {
  const messagesRef = collection(watchTrendRef(report), "messages");
  const snapshot = await getDocs(query(messagesRef, orderBy("createdAt", "desc")));

  return snapshot.docs.map(messageDocument => ({
    id: messageDocument.id,
    ...messageDocument.data()
  }));
};

export const addWatchTrendMessage = async (
  report,
  { text, authorId, authorName, intendedForClient }
) => {
  const trimmedText = String(text ?? "").trim();

  if (!trimmedText) {
    throw new Error("A Watchlist message cannot be empty.");
  }

  const messageRef = doc(collection(watchTrendRef(report), "messages"));
  const message = {
    schemaVersion: 1,
    id: messageRef.id,
    watchlistId: String(report.watchlistId),
    clientId: String(report.clientId),
    text: trimmedText,
    authorId: String(authorId ?? ""),
    authorName: String(authorName ?? ""),
    intendedForClient: intendedForClient === true,
    visibility: intendedForClient === true ? "client" : "internal",
    senderRole: "firm",
    deliveryStatus: "saved",
    createdAt: serverTimestamp()
  };

  await setDoc(messageRef, message);

  return {
    ...message,
    createdAt: Timestamp.now()
  };
};

export const markWatchTrendReviewed = async (
  report,
  { adminId, adminName }
) => {
  const safeAdminId = String(adminId ?? "").trim();
  const safeAdminName = String(adminName ?? "").trim();

  if (!safeAdminId) {
    throw new Error("An authenticated administrator is required.");
  }

  const reportRef = watchTrendRef(report);

  await updateDoc(reportRef, {
    needsReview: false,
    lastReviewedDate: serverTimestamp(),
    lastReviewedById: safeAdminId,
    lastReviewedByName: safeAdminName,
    reviewStateUpdatedAt: serverTimestamp(),
    reviewStateUpdatedById: safeAdminId,
    reviewStateUpdatedByName: safeAdminName
  });

  const reviewedSnapshot = await getDoc(reportRef);
  const reviewedData = reviewedSnapshot.data() ?? {};

  return {
    ...report,
    needsReview: reviewedData.needsReview ?? false,
    lastReviewedDate: reviewedData.lastReviewedDate ?? Timestamp.now(),
    lastReviewedById: String(reviewedData.lastReviewedById ?? safeAdminId),
    lastReviewedByName: String(reviewedData.lastReviewedByName ?? safeAdminName),
    reviewStateUpdatedAt: reviewedData.reviewStateUpdatedAt ?? Timestamp.now(),
    reviewStateUpdatedById: String(reviewedData.reviewStateUpdatedById ?? safeAdminId),
    reviewStateUpdatedByName: String(reviewedData.reviewStateUpdatedByName ?? safeAdminName)
  };
};

export const markWatchTrendNeedsReview = async (
  report,
  { adminId, adminName }
) => {
  const safeAdminId = String(adminId ?? "").trim();
  const safeAdminName = String(adminName ?? "").trim();

  if (!safeAdminId) {
    throw new Error("An authenticated administrator is required.");
  }

  const reportRef = watchTrendRef(report);

  await updateDoc(reportRef, {
    needsReview: true,
    reviewStateUpdatedAt: serverTimestamp(),
    reviewStateUpdatedById: safeAdminId,
    reviewStateUpdatedByName: safeAdminName
  });

  const reviewSnapshot = await getDoc(reportRef);
  const reviewData = reviewSnapshot.data() ?? {};

  return {
    ...report,
    needsReview: reviewData.needsReview ?? true,
    reviewStateUpdatedAt: reviewData.reviewStateUpdatedAt ?? Timestamp.now(),
    reviewStateUpdatedById: String(reviewData.reviewStateUpdatedById ?? safeAdminId),
    reviewStateUpdatedByName: String(reviewData.reviewStateUpdatedByName ?? safeAdminName)
  };
};

export const updateWatchTrendStatus = async (
  report,
  status,
  { adminId, adminName }
) => {
  const allowedStatuses = new Set(watchTrendWorkflowStatusValues);

  if (!allowedStatuses.has(status)) {
    throw new Error("The selected Watch trend status is invalid.");
  }

  const safeAdminId = String(adminId ?? "").trim();
  const safeAdminName = String(adminName ?? "").trim();

  if (!safeAdminId) {
    throw new Error("An authenticated administrator is required.");
  }

  const reportRef = watchTrendRef(report);
  const historyRef = doc(collection(reportRef, "statusHistory"));
  const batch = writeBatch(db);

  batch.update(reportRef, {
    status,
    statusUpdatedById: safeAdminId,
    statusUpdatedByName: safeAdminName,
    updatedAt: serverTimestamp()
  });

  batch.set(historyRef, {
    id: historyRef.id,
    watchlistId: String(report.watchlistId),
    clientId: String(report.clientId),
    fromStatus: String(report.status ?? "completed"),
    toStatus: status,
    changedById: safeAdminId,
    changedByName: safeAdminName,
    changedAt: serverTimestamp()
  });

  await batch.commit();

  return {
    ...report,
    status,
    statusUpdatedById: safeAdminId,
    statusUpdatedByName: safeAdminName,
    updatedAt: Timestamp.now()
  };
};

export const saveWatchlist = async (
  watchlistData
) => {
  try {
    const feedRef = collection(db, "admin", "watchlists", "feed");
    const watchlistRef = doc(feedRef);
    const watchlistId = watchlistRef.id;
    const publicationStatus = normalizeWatchlistPublicationStatus(
      watchlistData.publicationStatus,
      watchlistData.isPublished
    );
    const isPublished = isPublishedWatchlistStatus(publicationStatus);
    const proposedData = {
      schemaVersion: 1,
      watchlistId,
      name: String(watchlistData.name ?? "").trim(),
      description: String(watchlistData.description ?? "").trim(),
      tags: normalizeSearchMetadata(watchlistData.tags),
      searchTerms: normalizeSearchMetadata(watchlistData.searchTerms),
      explainerTitle: String(watchlistData.explainerTitle ?? "").trim(),
      explainerDescription:
        String(watchlistData.explainerDescription ?? "").trim(),
      bannerUrl: String(watchlistData.bannerUrl ?? "").trim(),
      publicationStatus,
      isPublished,
      questions: Array.isArray(watchlistData.questions) ?
        watchlistData.questions :
        [],
      submissionCount: 0,
      lastSubmissionAt: null,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
      publishedAt: isPublished ? Timestamp.now() : null
    };

    validateWatchlistDataContract(proposedData, watchlistId);

    await setDoc(watchlistRef, {
      ...proposedData,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      publishedAt: isPublished ? serverTimestamp() : null
    });

    const createdSnapshot = await getDoc(watchlistRef);

    validateWatchlistDataContract(
      createdSnapshot.data() ?? {},
      watchlistId
    );

    console.log("✅ Watchlist created:", watchlistId);

    return watchlistId;
  } catch (error) {
    console.error("❌ Error creating watchlist:", error);

    return null;
  }
};

export const updateWatchlist = async (watchlistId, watchlistData) => {
  try {
    if (!watchlistId) {
      throw new Error("A watchlist ID is required.");
    }

    const watchlistRef = doc(db, "admin", "watchlists", "feed", watchlistId);
    const watchlistSnapshot = await getDoc(watchlistRef);

    if (!watchlistSnapshot.exists()) {
      throw new Error("The Watchlist does not exist.");
    }

    const rawExistingData = watchlistSnapshot.data();
    const existingData = normalizeWatchlistDataContract(
      rawExistingData,
      watchlistId
    );

    validateWatchlistDataContract(existingData, watchlistId);

    const hasPublicationStatus =
      Object.prototype.hasOwnProperty.call(
        watchlistData,
        "publicationStatus"
      );
    const hasIsPublished =
      Object.prototype.hasOwnProperty.call(
        watchlistData,
        "isPublished"
      );
    const requestedPublicationStatus = hasPublicationStatus ?
      watchlistData.publicationStatus :
      hasIsPublished ?
        (watchlistData.isPublished ? "published" : "draft") :
        existingData.publicationStatus;
    const publicationStatus = normalizeWatchlistPublicationStatus(
      requestedPublicationStatus,
      watchlistData.isPublished,
      existingData.publicationStatus
    );
    const isPublished = isPublishedWatchlistStatus(publicationStatus);
    const valueOrExisting = (field, fallback) =>
      Object.prototype.hasOwnProperty.call(watchlistData, field) ?
        watchlistData[field] :
        fallback;

    const updates = {
      schemaVersion: 1,
      watchlistId,
      name: String(
        valueOrExisting("name", existingData.name) ?? ""
      ).trim(),
      description: String(
        valueOrExisting("description", existingData.description) ?? ""
      ).trim(),
      tags: normalizeSearchMetadata(
        valueOrExisting("tags", existingData.tags)
      ),
      searchTerms: normalizeSearchMetadata(
        valueOrExisting("searchTerms", existingData.searchTerms)
      ),
      explainerTitle: String(
        valueOrExisting("explainerTitle", existingData.explainerTitle) ?? ""
      ).trim(),
      explainerDescription: String(
        valueOrExisting(
          "explainerDescription",
          existingData.explainerDescription
        ) ?? ""
      ).trim(),
      bannerUrl: String(
        valueOrExisting("bannerUrl", existingData.bannerUrl) ?? ""
      ).trim(),
      publicationStatus,
      isPublished,
      questions: Array.isArray(watchlistData.questions) ?
        watchlistData.questions :
        existingData.questions,
      submissionCount: existingData.submissionCount,
      lastSubmissionAt: existingData.lastSubmissionAt,
      createdAt: existingData.createdAt,
      publishedAt: existingData.publishedAt,
      updatedAt: serverTimestamp()
    };

    if (isPublished && rawExistingData.publishedAt == null) {
      updates.publishedAt = serverTimestamp();
    }

    const proposedData = {
      ...updates,
      updatedAt: Timestamp.now(),
      publishedAt:
        isPublished && rawExistingData.publishedAt == null ?
          Timestamp.now() :
          updates.publishedAt
    };

    validateWatchlistDataContract(proposedData, watchlistId);

    await updateDoc(watchlistRef, updates);

    const updatedSnapshot = await getDoc(watchlistRef);

    validateWatchlistDataContract(
      updatedSnapshot.data() ?? {},
      watchlistId
    );

    console.log("✅ Watchlist updated:", watchlistId);

    return true;
  } catch (error) {
    console.error("❌ Error updating Watchlist:", error);

    return false;
  }
};

export const deleteWatchlist = async watchlistId => {
  try {
    if (!watchlistId) {
      throw new Error("A Watchlist ID is required.");
    }

    const watchlistRef = doc(db, "admin", "watchlists", "feed", watchlistId);
    const leaseRef = contentEditLeaseRef("watchlists", watchlistId);

    await runTransaction(db, async transaction => {
      const [watchlistSnapshot, leaseSnapshot] = await Promise.all([
        transaction.get(watchlistRef),
        transaction.get(leaseRef)
      ]);

      if (!watchlistSnapshot.exists()) {
        throw new Error("The Watchlist does not exist.");
      }

      const watchlist = normalizeWatchlistDataContract(
        watchlistSnapshot.data(),
        watchlistId
      );

      if (watchlist.publicationStatus !== "draft") {
        throw new Error("Only Draft Watchlists can be removed.");
      }

      if (watchlist.submissionCount > 0) {
        throw new Error("A Watchlist with submissions cannot be removed.");
      }

      transaction.delete(watchlistRef);
      if (leaseSnapshot.exists()) { transaction.delete(leaseRef); }
    });

    console.log("✅ Watchlist removed:", watchlistId);
    return true;
  } catch (error) {
    console.error("❌ Error removing Watchlist:", error);
    return false;
  }
};

// MARK: - 7. Watch Intelligence

export async function fetchUnsubmittedWatchSeeds({ pageSize = 200, cursor = null } = {}) {
  const db = getFirestore();
  const seedsCol = collection(doc(db, 'admin', 'watch'), 'watchlist');

  let q = query(
    seedsCol,
    where('submitted', '==', false),
    orderBy('lastUpdated', 'desc'),
    limit(pageSize)
  );
  if (cursor) q = query(q, startAfter(cursor));

  const snap = await getDocs(q);
  const items = snap.docs.map(d => {
    const raw = d.data() || {};
    const ts = raw.lastUpdated;
    const lastUpdated =
      ts && typeof ts.toDate === 'function'
        ? ts.toDate().toISOString()
        :
      ( typeof ts === 'string'
        ? ts
        : null);

    return {
      id: d.id,
      llfcid: raw.llfcid ?? null,
      origChatId: raw.origChatId ?? null,
      firstSubmitted: raw.firstSubmitted?.toDate?.()?.toISOString?.() ?? null,
      chatSlug: Array.isArray(raw.chatSlug) ? raw.chatSlug : [],
      companyMentioned: Array.isArray(raw.companyMentioned) ? raw.companyMentioned : [],
      issueSummary: raw.issueSummary ?? null,
      likelyLegalCategory: raw.likelyLegalCategory ?? null,
      tags: Array.isArray(raw.tags) ? raw.tags : [],
      patternFlags: Array.isArray(raw.patternFlags) ? raw.patternFlags : [],
      severityScore: typeof raw.severityScore === 'number' ? raw.severityScore : null,
      sentimentScore: typeof raw.sentimentScore === 'number' ? raw.sentimentScore : null,
      lastUpdated,
      isEscalated: typeof raw.isEscalated === 'boolean' ? raw.isEscalated : null,
      aiNormalizedSuggestions: Array.isArray(raw.aiNormalizedSuggestions)
        ? raw.aiNormalizedSuggestions
        : [],
      llfApprovedSuggestions: Array.isArray(raw.llfApprovedSuggestions)
        ? raw.llfApprovedSuggestions
        : [],
      normalizedCompanies: Array.isArray(raw.normalizedCompanies) ? raw.normalizedCompanies : [],
      evaluated: !!raw.evaluated,
      evaluatedAt: raw.evaluatedAt?.toDate?.()?.toISOString?.() ?? null,
      submitted: !!raw.submitted,
      submittedAt: raw.submittedAt?.toDate?.()?.toISOString?.() ?? null,
    };
  });

  const lastDoc = snap.docs.length ? snap.docs[snap.docs.length - 1] : null;
  return { items, lastDoc };
}

export async function fetchWatchingCompanies() {
  const db = getFirestore();
  const docRef = doc(db, 'admin', 'watch');
  const snap = await getDoc(docRef);
  const data = snap.exists() ? snap.data() : {};
  const watchingCos = Array.isArray(data.watchingCos) ? data.watchingCos : [];
  return watchingCos.map(s => (s ?? '').toString());
}

export async function fetchAllUnsubmittedWatchSeeds({ pageSize = 500 } = {}) {
  let cursor = null;
  const all = [];
  // cap loop to avoid infinite in weird cases
  for (let i = 0; i < 50; i++) {
    const { items, lastDoc } = await fetchUnsubmittedWatchSeeds({ pageSize, cursor });
    all.push(...items);
    if (!lastDoc) break;
    cursor = lastDoc;
  }
  return all;
}

export async function fetchChatStreamCount(clientId, chatId) {
  if (!clientId || !chatId) {
    console.warn("fetchChatStreamCount: missing clientId or chatId");
    return 0;
  }

  try {
    const streamCol = collection(db, "clients", clientId, "watchlist", chatId, "stream");
    const snap = await getDocs(streamCol);
    return snap.size;
  } catch (err) {
    console.error("fetchChatStreamCount error:", err);
    throw err;
  }
}

const slugify = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)+/g, '')
    .slice(0, 64);

export async function confirmCompanyAndAppendSeeds(params = {}) {
  const { companyCanonical, companyId: companyIdRaw, seeds = [], } = params;

  if (!companyCanonical && !companyIdRaw) {
    throw new Error('confirmCompanyAndAppendSeeds: companyCanonical or companyId is required.');
  }

  const companyId = companyIdRaw || slugify(companyCanonical);
  const now = serverTimestamp();

  // Store confirmed companies at /watch/{companyId}
  const companyRef = doc(db, 'admin', 'watch', 'watching', companyId);
  // Maintain dropdown array at /admin/watch.watchingCos
  const adminWatchRef = doc(db, 'admin', 'watch');
  const addCount = seeds.length;
  const addSum = seeds.reduce((n, s) => n + (Number(s.severityScore) || 0), 0);
  const localMax = Math.max(0, ...seeds.map(s => Number(s.severityScore) || 0));

  // 1) Upsert parent + counters; set createdAt only once
  await runTransaction(db, async (tx) => {
    const snap = await tx.get(companyRef);
    const exists = snap.exists();

    tx.set(
      companyRef,
      {
        companyId,
        companyCanonical: companyCanonical || companyId,
        seedCount: increment(addCount),
        sumSeverity: increment(addSum),
        lastMentionedAt: now,
        updatedAt: now,
        ...(exists ? {} : { createdAt: now }),
      },
      { merge: true }
    );

    tx.set(
      adminWatchRef,
      { watchingCos: arrayUnion(companyCanonical || companyId) },
      { merge: true }
    );
  });

  // 2) Append seeds under /watch/{companyId}/seeds
  const MAX_PER_BATCH = 450;
  for (let i = 0; i < seeds.length; i += MAX_PER_BATCH) {
    const group = seeds.slice(i, i + MAX_PER_BATCH);
    const b = writeBatch(db);

    group.forEach((s) => {
      const seedId =
        s.id ||
        s.origChatId ||
        slugify(`${s.issueSummary || 'seed'}-${Date.now()}-${Math.random()}`);
      const seedRef = doc(collection(companyRef, 'seeds'), seedId);

      b.set(
        seedRef,
        {
          seedId,
          // ensure watchId is the llfcid
          watchId: s.llfcid || s.clientId || null,
          // optional: keep clientId too if you use it elsewhere
          clientId: s.llfcid || s.clientId || null,
          issueSummary: s.issueSummary || '',
          likelyLegalCategory: s.likelyLegalCategory || '',
          severityScore: Number(s.severityScore) || 0,
          sentimentScore: Number(s.sentimentScore) || 0,

          // keep server time when missing; use provided timestamp if present
          lastUpdated: s.lastUpdated ? new Date(s.lastUpdated) : now,
          submitted: true, // immediately marked submitted
          active: true, // <-- per your request
          confirmedAt: now,
          updatedAt: now,
        },
        { merge: true }
      );
    });

    await b.commit();
  }

  // 3) Best-effort maxSeverity; CF can maintain perfectly later
  if (localMax > 0) {
    await runTransaction(db, async (tx) => {
      const snap = await tx.get(companyRef);
      const prev = Number(snap.data()?.maxSeverity || 0);
      const next = Math.max(prev, localMax);
      if (next !== prev) {
        tx.set(companyRef, { maxSeverity: next, updatedAt: now }, { merge: true });
      }
    });
  }

  return { companyId, added: addCount };
}

export async function fetchWatchingCosArray() {
  const snap = await getDoc(doc(db, 'admin', 'watch'));
  const data = snap.exists() ? snap.data() : {};
  const arr = Array.isArray(data.watchingCos) ? data.watchingCos : [];
  // normalize to strings
  return arr.map(s => (s ?? '').toString()).filter(Boolean);
}

/** One-shot: list of company docs under admin/watch/watching */
export async function fetchWatchingCompanyDocs() {
  const colRef = collection(db, 'admin', 'watch', 'watching');
  const snap = await getDocs(colRef);
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

/** Live subscription to admin/watch/watching; call `unsub()` on unmount */
export function watchWatchingCompanies(onChange) {
  const colRef = collection(db, 'admin', 'watch', 'watching');
  const q = query(colRef, orderBy('companyCanonical', 'asc'));
  return onSnapshot(q, (snap) => {
    const items = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    onChange(items);
  });
}

export function watchCompanySeeds(companyId, cb, { pageSize = 1000 } = {}) {
  if (!companyId) return () => {}; // no-op unsubscribe

  console.log("Watching this seed: ", companyId)

  const db = getFirestore();
  const seedsQ = query(
    collection(db, 'watch', 'watching', companyId, 'seeds'),
    orderBy('lastUpdated', 'desc'),
    limit(pageSize) // ← use the aliased operator
  );

  // Return the unsubscribe function
  return onSnapshot(seedsQ, (snap) => {
    const rows = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    cb(rows);
  });
}

export async function deleteCompanySeeds(companyId, seedIds) {
  const db = getFirestore();
  if (!companyId) throw new Error('deleteWatchSeeds: companyId required');

  const ids = (Array.isArray(seedIds) ? seedIds : [seedIds]).filter(Boolean);
  if (!ids.length) return 0;

  const CHUNK = 450;
  let total = 0;

  for (let i = 0; i < ids.length; i += CHUNK) {
    const slice = ids.slice(i, i + CHUNK);
    const batch = writeBatch(db);
    slice.forEach(id => {
      // 🔁 New path: admin/watch/watchlist/{companyId}/seeds/{id}
      batch.delete(doc(db, 'admin', 'watch', 'watchlist', id));
    });
    await batch.commit();
    total += slice.length;
  }
  return total;
}

export async function deleteUnconfirmedSeeds(seedIds) {
  const db = getFirestore();
  const ids = Array.isArray(seedIds) ? seedIds.filter(Boolean) : [seedIds].filter(Boolean);
  if (!ids.length) return 0;

  // chunk defensively (Firestore limit 500 ops)
  const CHUNK = 450;
  let total = 0;

  for (let i = 0; i < ids.length; i += CHUNK) {
    const slice = ids.slice(i, i + CHUNK);
    const batch = writeBatch(db);
    slice.forEach(id => {
      // path: admin (collection) / watch (doc) / watchlist (collection) / {id} (doc)
      batch.delete(doc(db, 'admin', 'watch', 'watchlist', id));
    });
    await batch.commit();
    total += slice.length;
  }

  return total;
}

// MARK: - 7. Dashboard Metrics

const fetchDashboardDocument = async documentName => {
  try {
    const snapshot = await getDoc(doc(db, "dashboard", documentName));

    return snapshot.exists() ? snapshot.data() : {};
  } catch (error) {
    console.warn(`⚠️ Unable to load dashboard/${documentName}:`, error);

    return {};
  }
};

const dashboardNumber = value => {
  const number = Number(value);

  return Number.isFinite(number) ? number : 0;
};

const dashboardPercentage = (value, total) => {
  const resolvedValue = dashboardNumber(value);
  const resolvedTotal = dashboardNumber(total);

  if (resolvedTotal <= 0) {
    return 0;
  }

  return Math.min(100, Math.max(0, (resolvedValue / resolvedTotal) * 100));
};

const dashboardDate = value => {
  if (!value) {
    return null;
  }

  if (typeof value.toDate === "function") {
    return value.toDate();
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date;
};

const fetchRecentSignInCounts = async () => {
  try {
    const signInsRef = collection(db, "dashboard", "appClients", "signIns");
    const now = Date.now();
    const thirtyDayCutoff = Timestamp.fromMillis(now - 30 * 24 * 60 * 60 * 1000);
    const thirtyDayQuery = query(signInsRef, where("lastSignInAt", ">=", thirtyDayCutoff));
    const thirtyDaySnapshot = await getCountFromServer(thirtyDayQuery);

    return {
      activeClients30Days: dashboardNumber(thirtyDaySnapshot.data().count)
    };
  } catch (error) {
    console.warn("⚠️ Unable to load App Client sign-in counts:", error);

    return {
      activeClients30Days: 0
    };
  }
};

const fetchAttorneyRequestCounts = async () => {
  try {
    const requestsRef = collection(db, "requests");
    const newRequestsQuery = query(requestsRef, where("updatedAt", "==", null));
    const newRequestsSnapshot = await getCountFromServer(newRequestsQuery);

    return {
      newAndUnanswered: dashboardNumber(newRequestsSnapshot.data().count)
    };
  } catch (error) {
    console.warn("⚠️ Unable to load Attorney Request counts:", error);

    return {
      newAndUnanswered: 0
    };
  }
};

export const fetchAppClientDashboard = async () => {
  const [
    appClients,
    litigationApplications,
    watchlistSessions,
    maixConvos,
    watchConvos,
    requests,
    signInCounts,
    attorneyRequestCounts,
    supportTicketCounts,
    unopenedSupportTickets
  ] = await Promise.all([
    fetchDashboardDocument("appClients"),
    fetchDashboardDocument("litigationApplications"),
    fetchDashboardDocument("watchlistSessions"),
    fetchDashboardDocument("maixConvos"),
    fetchDashboardDocument("watchConvos"),
    fetchDashboardDocument("requests"),
    fetchRecentSignInCounts(),
    fetchAttorneyRequestCounts(),
    fetchSupportTicketCounts(),
    fetchUnopenedSupportTickets()
  ]);

  const updatedDates = [
    appClients.updatedAt,
    litigationApplications.updatedAt,
    watchlistSessions.updatedAt,
    maixConvos.updatedAt,
    watchConvos.updatedAt,
    requests.updatedAt
  ]
    .map(dashboardDate)
    .filter(Boolean);

  const lastUpdatedAt =
    updatedDates.length > 0
      ? new Date(Math.max(...updatedDates.map(date => date.getTime())))
      : null;

  return {
    counts: {
      createdClients: dashboardNumber(appClients.createdClients),
      clients: dashboardNumber(appClients.activeClients),
      archivedClients: dashboardNumber(appClients.archivedClients),
      activeClients30Days: dashboardNumber(signInCounts.activeClients30Days),
      activeClients30DayPercent: dashboardPercentage(
        signInCounts.activeClients30Days,
        appClients.activeClients
      ),
      openSupportTickets: dashboardNumber(supportTicketCounts.openTickets),
      unopenedSupportTickets: unopenedSupportTickets.length,
      attorneyRequests: dashboardNumber(requests.totalCount ?? requests.total),
      newAttorneyRequests: dashboardNumber(attorneyRequestCounts.newAndUnanswered),
      litigationApplicationsStarted: dashboardNumber(litigationApplications.startedCount),
      litigationApplicationsCompleted: dashboardNumber(litigationApplications.completedCount),
      watchlistSessionsStarted: dashboardNumber(watchlistSessions.startedCount),
      watchlistSessionsCompleted: dashboardNumber(
        watchlistSessions.submittedCount ?? watchlistSessions.completedCount
      ),
      maixChatsStarted: dashboardNumber(maixConvos.startedCount),
      maixChatsActivated: dashboardNumber(maixConvos.activatedCount),
      watchChatsStarted: dashboardNumber(watchConvos.startedCount),
      watchChatsActivated: dashboardNumber(watchConvos.activatedCount)
    },
    supportTickets: {
      unopened: unopenedSupportTickets
    },
    lastUpdatedAt,
    raw: {
      appClients,
      litigationApplications,
      watchlistSessions,
      maixConvos,
      watchConvos,
      requests,
      signInCounts,
      attorneyRequestCounts,
      supportTicketCounts
    }
  };
};


// MARK: SOON TO BE OBSOLETE - KEEP FOR PERSISTANCE
// MARK: - Lawyers

export const fetchAttyData = async (attyEmail) => {
  try {
    const lawyersRef = collection(db, "lawyers");
    const q = query(lawyersRef, where("attyEm", "==", attyEmail));
    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty || querySnapshot.size !== 1) {
      console.error(`Invalid number of matches: ${querySnapshot.size}.`);
      return null;
    }

    const attyData = querySnapshot.docs[0].data();
    const attyId = querySnapshot.docs[0].id

    return { ...attyData, id: attyId };
  } catch (error) {
    console.error("Error fetching attorney data:", error.message);
    throw error;
  }
};

export const fetchAttyIdData = async (fetchId) => {
  try {
    if (!fetchId) {
      console.error("❌ Invalid ID: fetchId is undefined or null.");
      return null;
    }

    const lawyerRef = doc(db, "lawyers", fetchId);
    const lawyerDoc = await getDoc(lawyerRef);

    if (!lawyerDoc.exists()) {
      console.error(`❌ No document found for ID: ${fetchId}`);
      return null;
    }

    const attyData = lawyerDoc.data();
    console.log("✅ Fetched Attorney:", { id: lawyerDoc.id, ...attyData });

    return { id: lawyerDoc.id, ...attyData };
  } catch (error) {
    console.error("❌ Error fetching attorney data:", error.message);
    throw error;
  }
};

export const checkAttyEmails = async (attyEm) => {
  try {
    const authRef = doc(db, "admin", "auths");
    const authDoc = await getDoc(authRef);

    if (!authDoc.exists()) {
      console.warn("⚠️ admin/auths document does not exist.");
      return false;
    }

    const data = authDoc.data();
    const attyEms = data?.attyEms || [];

    return attyEms.includes(attyEm);
  } catch (error) {
    console.error("❌ Error checking email:", error.message);
    throw error;
  }
};

export const saveAttorneyRequest = async (reqData) => {
  try {
    const docRef = await addDoc(collection(db, "admin/watch/attyReqs"), reqData);
    console.log(`✅ Attorney data request sent...`);
    return docRef.id
  } catch (error) {
    console.error("❌ Error sending request:", error.message);
    throw error;
  }
};

export const updateAttorney = async (attyId, updatedData) => {
  try {
    if (!attyId) throw new Error("Invalid attorney document ID.");
    if (!updatedData || Object.keys(updatedData).length === 0) {
      throw new Error("No valid fields to update.");
    }

    const attyRef = doc(db, "lawyers", attyId);
    await updateDoc(attyRef, updatedData);

    console.log(
      `✅ Attorney updated successfully. Active Status: ${updatedData.active}`,
      updatedData
    );
    return true;
  } catch (error) {
    console.error("❌ Error updating attorney:", error.message);
    return false;
  }
};

export const fetchNewAttyRequests = async () => {
  try {
    const twelveHoursAgo = Timestamp.fromMillis(Date.now() - 12 * 60 * 60 * 1000);
    const querySnapshot = await getDocs(
      query(collection(db, "admin/watch/attyReqs"), where("createdAt", ">=", twelveHoursAgo))
    );

    console.log("Reqs:", querySnapshot.size); // ✅ Log how many requests were found

    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
  } catch (error) {
    console.error("❌ Error fetching new attorney requests:", error.message);
    return [];
  }
};

// MARK: - Chats & Messaging

export async function fetchChats(attyId) {
  try {
    console.log("Getting Chats for " + attyId)

    const chatsRef = collection(db, "chats");
    const q = query(chatsRef, where("chatOccs", "array-contains", attyId));
    const querySnapshot = await getDocs(q);
    const chats = querySnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    chats.forEach((chat) => {
      console.log("Chat Fetched: " + chat.id)
    })

    return chats;
  } catch (err) {
    console.error("❌ Error fetching chats:", err);
    throw err; // Throw the error so it can be handled where the function is called
  }
}

export async function fetchClientChat(clientId, chatId) {
  try {
    const chatRef = doc(db, `clients/${clientId}/chats/${chatId}`);
    const chatSnap = await getDoc(chatRef);

    if (chatSnap.exists()) {
      console.log("✅ Chat Data Fetched:", chatSnap.data());
      return chatSnap.data();
    } else {
      console.warn("⚠️ No chat found for this ID.");
      return null;
    }
  } catch (error) {
    console.error("❌ Error fetching chat:", error);
    return null;
  }
}

export const handleSendMessage = async (clientId, deatsId, newMessage, attyCheckIn) => {
  try {
    console.log("🔹 Sending message:", newMessage);

    const mainChatRef = doc(db, "chats", deatsId);
    const clientChatRef = doc(db, `clients/${clientId}/chats/${newMessage.chatId}`);
    const streamCollectionRef = collection(clientChatRef, "stream");
    const latestQuerySnap = await getDocs(
      query(streamCollectionRef, orderBy("sentStamp", "desc"), limit(1))
    );

    let nextIdNumber = 1;

    if (!latestQuerySnap.empty) {
      const lastMsg = latestQuerySnap.docs[0].data();
      const [numStr] = (lastMsg.msgId || "0000").split(":");
      const parsedNum = parseInt(numStr, 10);
      nextIdNumber = isNaN(parsedNum) ? 1 : parsedNum + 1;
    }

    const paddedId = String(nextIdNumber).padStart(4, "0");
    newMessage.msgId = `${paddedId}:${newMessage.senderId}`;
    newMessage.sentStamp = serverTimestamp();

    // ✅ Add to stream
    await addDoc(streamCollectionRef, newMessage);

    console.log(`✅ Message written to stream with msgId ${newMessage.msgId}`);

    await runTransaction(db, async (transaction) => {
      const chatDoc = await transaction.get(mainChatRef);

      if (!chatDoc.exists()) throw new Error("❌ Main chat doc missing!");

      const data = chatDoc.data();
      const checkIns = data.chatCheckIns || [];
      const shouldAddSender = attyCheckIn || !checkIns.includes(newMessage.senderId);
      const updatePayload = {
        lastUpdated: serverTimestamp(),
        ...(shouldAddSender && { chatCheckIns: arrayUnion(newMessage.senderId) })
      };

      transaction.update(mainChatRef, updatePayload);
    });
  } catch (error) {
    console.error("❌ Error sending message:", error);
  }
};

export const sendLitChatReqMessage = async (litigationId, clientId, messagePayload) => {
  if (!litigationId || !clientId) {
    console.error("❌ Missing litigationId or clientId for chatReq message.");
    return;
  }

  try {
    const chatReqRef = collection(
      db,
      `admin/lits/feed/${litigationId}/clients/${clientId}/chatReq`
    );

    await addDoc(chatReqRef, {
      ...messagePayload,
      sentStamp: serverTimestamp(),
    });

    console.log("✅ chatReq message sent.");
  } catch (err) {
    console.error("❌ Failed to send chatReq message:", err);
  }
};

export const listenToChatUpdates = (clientId, chatId, callback) => {
  if (!clientId || !chatId) {
    console.error("❌ Missing clientId or chatId for Firestore listener.");
    return;
  }

  console.log(`📡 Listening for subcollection chatMsgs in chat ${chatId} for client ${clientId}`);

  const chatMsgsRef = collection(db, `clients/${clientId}/chats/${chatId}/stream`);
  const q = query(chatMsgsRef, orderBy("sentStamp", "asc")); // sort oldest → newest

  const unsubscribe = onSnapshot(q, (snapshot) => {
    const messages = [];
    snapshot.forEach((doc) => {
      messages.push({ id: doc.id, ...doc.data() });
    });
    console.log("📨 chatMsgs snapshot update:", messages);
    callback(messages); // Return as array of ChatMsg
  });

  return unsubscribe;
};

export const listenToLitChatReq = (clientId, litigationId, callback) => {
  console.log("Listening to Client: " + clientId)
  console.log("Listening to Lit: " + litigationId)

  if (!litigationId || !clientId) {
    console.error("❌ Missing litigationId or clientId for chatReq listener.");
    return;
  }

  const chatReqRef = collection(db, `admin/lits/feed/${litigationId}/clients/${clientId}/chatReq`);
  const q = query(chatReqRef, orderBy("sentStamp", "asc"));
  const unsubscribe = onSnapshot(q, (snapshot) => {
    const chatMsgs = [];
    snapshot.forEach((doc) => {
      chatMsgs.push({ ...doc.data() });
    });
    console.log("📡 chatReq update:", chatMsgs);
    callback(chatMsgs);
  });

  return unsubscribe;
};

// MARK: - Widgets & Data Explorer

export const saveWidgetToFirestore = async (adminId, widget) => {
  const docRef = collection(db, `admin/auths/widgets`);
  await addDoc(docRef, {
    ...widget
  });
};

// export const fetchWidgets = async () => {
//   try {
//     const snapshot = await getDocs(collection(db, "admin/auths/widgets"));
//     return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
//   } catch (error) {
//     console.error("❌ Error fetching widgets:", error);
//     return [];
//   }
// };

export const fetchWidgets = async () => {
  const widgetsRef = collection(db, 'admin/auths/widgets');
  const widgetSnapshots = await getDocs(widgetsRef);
  const widgets = [];

  for (const snap of widgetSnapshots.docs) {
    const widget = snap.data();
    const typeTitle = widget.type;
    const bucket = buckets.find(b => b.title === typeTitle);

    if (!bucket) continue;

    const lookupPath = bucket.lookup;
    const dataQuery = query(collection(db, lookupPath), limit(widget.showInt || 3));
    const dataSnap = await getDocs(dataQuery);
    const widgetData = [];

    dataSnap.forEach(docSnap => {
      const docData = docSnap.data();
      widgetData.push({
        id: docSnap.id,
        [widget.dataOne]: docData[widget.dataOne] || '',
        [widget.dataTwo]: widget.dataTwo ? docData[widget.dataTwo] || '' : '',
        [widget.dataThree]: widget.dataThree ? docData[widget.dataThree] || '' : '',
      });
    });

    widget.data = widgetData;
    widgets.push(widget);
  }

  return widgets;
};

export const fetchWidgetDocs = async (lookupPath, pageSize = 25, lastDoc = null) => {
  try {
    const colRef = collection(db, `admin/${lookupPath}`);
    const q = lastDoc
      ? query(colRef, orderBy("createdAt", "desc"), startAfter(lastDoc), limit(pageSize))
      : query(colRef, orderBy("createdAt", "desc"), limit(pageSize));

    const snapshot = await getDocs(q);
    const docs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    return {
      docs,
      lastVisible: snapshot.docs[snapshot.docs.length - 1] || null,
      firstVisible: snapshot.docs[0] || null
    };
  } catch (err) {
    console.error("❌ Error fetching widget docs:", err);
    return { docs: [], lastVisible: null, firstVisible: null };
  }
};

export const getBucketData = async (
  lookupPath,
  pageSize = 25,
  startDoc = null,
  endDoc = null,
  orderByField = null,
  fullLimit = 25,
  currentPageIndex = 1
) => {
  try {
    console.log("Looking in:", lookupPath, "| Ordered by:", orderByField);
    const colRef = collection(db, lookupPath);

    let q;
    let paginated = !!orderByField;

    if (paginated) {
      // Standard paginated ordering
      if (startDoc) {
        q = query(colRef, orderBy(orderByField, "desc"), startAfter(startDoc), limit(pageSize));
      } else if (endDoc) {
        q = query(colRef, orderBy(orderByField, "desc"), endBefore(endDoc), limit(pageSize));
      } else {
        q = query(colRef, orderBy(orderByField, "desc"), limit(pageSize));
      }
    } else {
      // Just get full batch for offset-based slicing
      q = query(colRef, limit(fullLimit));
    }

    const snapshot = await getDocs(q);
    const allDocs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    let docs = allDocs;

    if (!paginated) {
      const start = (currentPageIndex - 1) * pageSize;
      const end = start + pageSize;
      docs = allDocs.slice(start, end);
    }

    return {
      docs,
      first: snapshot.docs[0] || null,
      last: snapshot.docs[snapshot.docs.length - 1] || null,
      isPaginated: paginated
    };
  } catch (error) {
    console.error("❌ Error fetching bucket data:", error);
    return { docs: [], first: null, last: null, isPaginated: false };
  }
};

export const updateDocumentField = async (path, docId, fieldKey, newValue) => {
  try {
    const docRef = doc(db, path, docId);
    await updateDoc(docRef, {
      [fieldKey]: newValue
    });
    console.log(`✅ Updated ${fieldKey} in ${path}/${docId}`);
  } catch (error) {
    console.error("❌ Error updating document field:", error);
    throw error;
  }
};

export const fetchAuthorizedLitigations = async (userId) => {
  const matchedLits = [];

  try {
    const firmsRef = collection(db, "firms");
    const firmsQuery = query(firmsRef, where("authList", "array-contains", userId));
    const firmsSnapshot = await getDocs(firmsQuery);

    if (firmsSnapshot.empty) {
      console.warn("⚠️ No firms found where user is authorized.");
      return [];
    }

    const firmData = firmsSnapshot.docs[0].data();
    const litsList = firmData.litList || [];

    for (const litId of litsList) {
      console.log("Searching for: " + litId)
      const attysRef = collection(db, `admin/lits/feed/${litId}/attys`);
      const attysQuery = query(attysRef, where("authList", "array-contains", userId));
      const attysSnapshot = await getDocs(attysQuery);

      if (!attysSnapshot.empty) {
        console.log("Found Lits")
        // ✅ Now fetch the full litigation document
        const litRef = doc(db, `admin/lits/feed/${litId}`);
        const litSnap = await getDoc(litRef);

        if (litSnap.exists()) {
          matchedLits.push({
            id: litSnap.id,
            ...litSnap.data()
          });
        } else {
          console.warn(`⚠️ Litigation doc not found for: ${litId}`);
        }
      }
    }

    return matchedLits; // ✅ Return the array of full litigation objects
  } catch (error) {
    console.error("❌ Error fetching authorized litigations:", error);
    return [];
  }
};

export const fetchLitigationCases = async (litigationId) => {
  try {
    const casesRef = collection(db, `admin/lits/feed/${litigationId}/clients`);
    const snapshot = await getDocs(casesRef);
    const cases = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    return cases;
  } catch (error) {
    console.error("❌ Error fetching litigation cases:", error);
    return [];
  }
};

export const fetchLitigationClients = async (litId) => {
  try {
    const casesList = [];
    const clientsRef = collection(db, `admin/lits/feed/${litId}/clients`);
    const clientsSnap = await getDocs(clientsRef);

    for (const docSnap of clientsSnap.docs) {
      const data = docSnap.data();
      const submission = { id: docSnap.id, ...data }; // Full LitSubmission

      const caseObj = {
        caseTitle: submission.subJurisdiction,
        caseStatus: submission.status?.label || "Unknown",
        lastUpdated: submission.lastUpdated?.toDate() || new Date(),
        fullSubmission: submission,
        chatRequests: null // defer fetching
      };

      casesList.push(caseObj);
    }

    // 🔥 Sort by lastUpdated (descending) first
    const steps = ["Submitted", "In Review", "More Info Needed", "Accepted", "Filed", "Resolved"];

    casesList.sort((a, b) => {
      const dateDiff = new Date(b.lastUpdated) - new Date(a.lastUpdated);
      if (dateDiff !== 0) return dateDiff;

      // 🔥 If same date, sort by status order
      const stepOrder = (status) =>
        steps.indexOf(status) !== -1 ? steps.indexOf(status) : steps.length;
      return stepOrder(a.caseStatus) - stepOrder(b.caseStatus);
    });

    return casesList;
  } catch (error) {
    console.error("❌ Error fetching litigation clients:", error);
    return [];
  }
};

export const updateLitFeedMeta = async (litId, updates) => {
  const ref = doc(db, 'admin/lits/feed', litId);
  await updateDoc(ref, updates);
};

export const updateAttentionCountForLitigation = async (litId) => {
  try {
    const clientsRef = collection(db, `admin/lits/${litId}/clients`);
    const q = query(clientsRef, where("status.label", "in", ["SUBMITTED", "IN REVIEW"]));
    const snapshot = await getDocs(q);
    const attentionCount = snapshot.size;
    const feedRef = doc(db, "admin/lits/feed", litId);
    await updateDoc(feedRef, { xSubmitted: attentionCount });

    return attentionCount;
  } catch (err) {
    console.error(`❌ Error updating xSubmitted for ${litId}:`, err);
    return 0;
  }
};

export const fetchClientChatRequests = async (litId, clientId) => {
  try {
    const chatReqsRef = collection(db, `admin/lits/feed/${litId}/clients/${clientId}/chatReqs`);
    const chatReqsSnap = await getDocs(chatReqsRef);

    return chatReqsSnap.docs.map(c => ({
      id: c.id,
      ...c.data()
    }));
  } catch (error) {
    console.error("❌ Error fetching chat requests:", error);
    return [];
  }
};

export const fetchClientInfo = async (clientId) => {
  try {
    const docRef = doc(db, "clients", clientId);
    const snapshot = await getDoc(docRef);

    if (!snapshot.exists()) {
      console.warn("⚠️ Client not found:", clientId);
      return null;
    }

    return { id: snapshot.id, ...snapshot.data() };
  } catch (error) {
    console.error("❌ Error fetching client info:", error);
    return null;
  }
};

export const setMaixConversationArchived = async data => {
  const result = await httpsCallable(projectFunctions, "setMaixConversationArchived")(data);
  return result.data;
};

export const fetchAppClientStatAdditions = async () => {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const result = await httpsCallable(projectFunctions, "getAppClientStatAdditions", { timeout: 120000 })({ todayStart: today.getTime() });
  return result.data;
};

export const generateMaixMagicResponse = async messages => (await httpsCallable(projectFunctions, "generateMaixMagicResponse", { timeout: 120000 })({ messages })).data;
export const saveMaixMagicResponse = async response => (await httpsCallable(projectFunctions, "saveMaixMagicResponse", { timeout: 60000 })(response)).data;
export const listMaixMagicResponses = async filters => (await httpsCallable(projectFunctions, "listMaixMagicResponses", { timeout: 120000 })(filters)).data;

export const listSystemNotifications = async () => (await httpsCallable(projectFunctions, "listSystemNotifications")({})).data;
export const updateSystemNotificationSubscription = async (typeId, emailSubscribed) => (await httpsCallable(projectFunctions, "updateSystemNotificationSubscription")({ typeId, emailSubscribed })).data;

export const setSystemEmailActive = async data => (await httpsCallable(projectFunctions, "setSystemEmailActive")(data)).data;
